import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTheme } from "styled-components";
import { ModeButton, SidebarLabel } from "./ui";
import {
  Container,
  Toolbar,
  ToolbarGroup,
  ToolbarDivider,
  EditWindow,
  BoardScroller,
  Board,
  Row,
  Sidebar,
  EntityDot,
} from "./components/EditorLayout";
import { PaintControls } from "./paint-controls";
import { drawTileCellRect } from "../lib/tile-renderer";
import { FLOOR_TILES, getStamp, getSheet } from "../lib/tilesets";
import { useGetQuery, useToast } from "hooks";
import { useStateValue } from "providers";
import {
  createDungeon,
  updateDungeon,
  createHostDungeonNpc,
  updateHostDungeonNpc,
  deleteHostDungeonNpc,
  getHostLocations,
  createAdminDungeon,
  updateAdminDungeon,
  getAdminLocations,
  createAdminDungeonNpc,
  updateAdminDungeonNpc,
  deleteAdminDungeonNpc,
} from "actions/api";
import { generateDungeon, blankTiles } from "./generate-dungeon";
import { GRID_SIZE, ITEM_TYPES, MONSTER_SPRITES, spriteImagePath } from "../config/constants";
import { DialogEditor } from "./dialog-editor";
import { DockablePanel } from "./dockable-panel";

const STORAGE_KEY = "dungeon_map";

const MODES = ["tiles", "decoration", "overlay", "monsters", "npcs", "items", "ladders", "exits"];

const ENTITY_DEFAULTS = {
  monsters: {
    entityType: "monster",
    backgroundColor: "#8B0000",
    damage: 5,
    health: 30,
    stride: 1,
  },
  npcs: {
    entityType: "npc",
    backgroundColor: "#4488cc",
    dialogs: [{ lines: ["Hello!"], repeat: false }],
  },
  items: {
    entityType: "item",
    backgroundColor: "#FFD700",
    value: 10,
    treasureType: "gold",
    treasureBehavior: "normal",
  },
  ladders: {
    entityType: "ladder",
    backgroundColor: "#c8a84b",
    direction: "down",
    targetLevel: 1,
    targetX: 40,
    targetY: 40,
  },
  exits: { entityType: "exit", backgroundColor: "#44cc66" },
};

const ITEM_BEHAVIORS = [
  { value: "normal", label: "Normal (always available)" },
  { value: "character_unique", label: "Once per character" },
  { value: "user_unique", label: "Once per user" },
  { value: "global_unique", label: "Once globally" },
];

const TileMarker = ({ tile, grid, color, glyph, dashed }) => (
  <div
    style={{
      position: "absolute",
      left: tile.x * grid,
      top: tile.y * grid,
      width: grid,
      height: grid,
      border: `2px ${dashed ? "dashed" : "solid"} ${color}`,
      background: `${color}${dashed ? "22" : "33"}`,
      pointerEvents: "none",
      zIndex: 5,
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 9,
      color,
      fontFamily: "Silkscreen, monospace",
    }}
  >
    {glyph}
  </div>
);

const setLocal = (data) =>
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));

const tolevels = (d) => {
  if (d.levels) return d.levels;
  return [
    { name: "Level 1", tiles: d.tiles || [], entities: d.entities || [], decorationTiles: d.decorationTiles || [], overlayTiles: d.overlayTiles || [] },
  ];
};

const DEFAULT_ROWS = 20;
const DEFAULT_COLS = 20;

const getLocal = () => {
  const local = localStorage.getItem(STORAGE_KEY);
  if (local) {
    const parsed = JSON.parse(local);
    return { ...parsed, levels: tolevels(parsed) };
  }
  return {
    grid: GRID_SIZE,
    cols: DEFAULT_COLS,
    levels: [
      {
        name: "Level 1",
        tiles: blankTiles(DEFAULT_ROWS, DEFAULT_COLS),
        entities: [],
        decorationTiles: blankTiles(DEFAULT_ROWS, DEFAULT_COLS),
        overlayTiles: blankTiles(DEFAULT_ROWS, DEFAULT_COLS),
      },
    ],
  };
};

export const PinmonkeyEditor = () => {
  const { slug: urlSlug } = useParams();
  const id = urlSlug;
  const [{ session }] = useStateValue();
  const isAdmin = session?.user?.roles?.some((r) => r.slug === "admin");
  const dungeonNpcCreate = isAdmin
    ? createAdminDungeonNpc
    : createHostDungeonNpc;
  const dungeonNpcUpdate = isAdmin
    ? updateAdminDungeonNpc
    : updateHostDungeonNpc;
  const dungeonNpcDelete = isAdmin
    ? deleteAdminDungeonNpc
    : deleteHostDungeonNpc;
  const { colors } = useTheme();

  // Reusable inline-style tokens derived from the theme
  const c = {
    text: colors("highlight"),
    muted: colors("highlight", 0.55),
    border: colors("highlight", 0.3),
    innerBorder: colors("highlight", 0.15),
    bg: colors("background"),
    chrome: colors("chrome"),
    danger: colors("danger"),
    success: colors("success", 0.5),
    dim: colors("highlight", 0.3),
  };
  const inputStyle = {
    background: c.bg,
    border: `1px solid ${c.border}`,
    color: c.text,
    padding: "0.3rem 0.5rem",
    fontFamily: "Silkscreen, monospace",
  };
  const smallInput = {
    ...inputStyle,
    padding: "0.2rem 0.4rem",
    fontSize: "0.65rem",
  };
  const tinyInput = {
    ...inputStyle,
    padding: "0.2rem 0.4rem",
    fontSize: "0.6rem",
  };

  const [map, setMap] = useState(() => {
    if (!id) return { grid: GRID_SIZE, cols: DEFAULT_COLS };
    const d = getLocal();
    return { grid: d.grid || GRID_SIZE, cols: d.cols };
  });
  const [levels, setLevels] = useState(() => {
    if (!id)
      return [
        {
          name: "Level 1",
          tiles: blankTiles(DEFAULT_ROWS, DEFAULT_COLS),
          entities: [],
          decorationTiles: blankTiles(DEFAULT_ROWS, DEFAULT_COLS),
          overlayTiles: blankTiles(DEFAULT_ROWS, DEFAULT_COLS),
        },
      ];
    return getLocal().levels;
  });
  const [currentLevelIdx, setCurrentLevelIdx] = useState(0);
  const [mode, setMode] = useState("tiles");
  const [onlyShowCurrentLayer, setOnlyShowCurrentLayer] = useState(false);
  const [panMode, setPanMode] = useState(false);
  const [color, setColor] = useState("#2a2a2a");
  const [solid, setSolid] = useState(false);
  const [isPickingColor, setIsPickingColor] = useState(false);
  const [tileMode, setTileMode] = useState("color"); // 'color' | 'tileset'
  // selectedBrush: { kind: 'flat', sheet, tx, ty } | { kind: 'stamp', stampId }
  const [selectedBrush, setSelectedBrush] = useState({ kind: "flat", sheet: FLOOR_TILES[0].sheet, tx: FLOOR_TILES[0].tx, ty: FLOOR_TILES[0].ty });
  // For flat tileset brushes: paints a w x h patch of the sheet (anchored to absolute grid
  // coords) instead of repeating one 48px tile, so drag-filled floors/walls don't look stamped.
  const [patternSize, setPatternSize] = useState({ w: 2, h: 2 });
  // Mirrors the current brush horizontally — persists across tile/stamp picks, like patternSize.
  const [flipX, setFlipX] = useState(false);
  // Copy/paste: while copyMode is on, the next drag captures a region into `clipboard`
  // (then flips the brush to 'clipboard' so a normal click pastes it, same as a stamp).
  const [copyMode, setCopyMode] = useState(false);
  const [clipboard, setClipboard] = useState(null); // { w, h, cells }
  const [editingMonsterId, setEditingMonsterId] = useState(null);
  const [editingNpcId, setEditingNpcId] = useState(null);
  // Tile-pick mode: { id: placementId, areaIndex, key: 'tile' | 'npc_position' }
  const [pickingTileFor, setPickingTileFor] = useState(null);
  const [npcPlacements, setNpcPlacements] = useState([]);
  const [selectedNpcId, setSelectedNpcId] = useState(null); // DB NPC selected for next placement
  const [dungeonUUID, setDungeonUUID] = useState(null);
  const npcPlacementsRef = useRef([]);
  const dungeonUUIDRef = useRef(null);
  useEffect(() => {
    npcPlacementsRef.current = npcPlacements;
  }, [npcPlacements]);
  useEffect(() => {
    dungeonUUIDRef.current = dungeonUUID;
  }, [dungeonUUID]);
  const [editingLadderId, setEditingLadderId] = useState(null);
  const [editingItemId, setEditingItemId] = useState(null);
  const [selectedItemType, setSelectedItemType] = useState(ITEM_TYPES[0]);
  const [overlayErase, setOverlayErase] = useState(false);
  const [overlayOpacity, setOverlayOpacity] = useState(1);
  const [decorationErase, setDecorationErase] = useState(false);
  const [decorationOpacity, setDecorationOpacity] = useState(1);
  const [floodFill, setFloodFill] = useState(false);

  // Undo history — stores snapshots of `levels` before each destructive edit
  const historyRef = useRef([]);
  const levelsRef = useRef(levels);
  useEffect(() => { levelsRef.current = levels }, [levels]);

  const pushHistory = () => {
    const snap = levelsRef.current;
    historyRef.current = [...historyRef.current.slice(-49), snap];
  };

  // Drag-to-fill state
  const [dragStart, setDragStart] = useState(null); // {row, col}
  const [dragEnd, setDragEnd] = useState(null); // {row, col}
  const boardRef = useRef(null);
  const boardCanvasRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const editorImageCacheRef = useRef({});

  // Whole-stamp select/erase/move — click a placed stamp to pick it up; release without
  // moving to erase it, release after dragging to move it (clipped to map bounds).
  const stampDragRef = useRef(null); // { stampId, originCol, originRow, w, h, startClientX, startClientY, hasMoved }
  const [stampDragPreview, setStampDragPreview] = useState(null); // { col, row, w, h }

  // Hover tooltip — what's under the cursor while not dragging/painting.
  const [hoverInfo, setHoverInfo] = useState(null); // { clientX, clientY, text }
  // Footprint outline shown at the cursor before a stamp/clipboard paste is placed.
  const [placementPreview, setPlacementPreview] = useState(null); // { col, row, w, h }

  // Entity drag state
  const entityDragRef = useRef(null); // { id, startClientX, startClientY, hasMoved }
  const entityDragInitiatedRef = useRef(false); // suppress click after a drag
  const [dragEntityPreview, setDragEntityPreview] = useState(null); // { entity, x, y }

  // Boundary draw/resize state (boundary stored as { x, y, w, h } in tiles)
  const boundaryResizeRef = useRef(null); // { entityId, anchorTileX, anchorTileY }
  const boundaryDrawRef = useRef(null); // { entityId } — drawing a new boundary by drag
  const boundaryDrawInitiatedRef = useRef(false); // suppress entity click after a boundary draw
  const [boundaryDrawPreview, setBoundaryDrawPreview] = useState(null); // { startTile, endTile }

  // Derive tiles/entities/decorationTiles/overlayTiles from the active level
  const currentLevel = levels[currentLevelIdx] ?? levels[0];
  const tiles = currentLevel.tiles;
  const entities = currentLevel.entities;
  const decorationTiles = currentLevel.decorationTiles ?? blankTiles(tiles.length, tiles[0]?.length ?? 0);
  const overlayTiles = currentLevel.overlayTiles ?? blankTiles(tiles.length, tiles[0]?.length ?? 0);

  // Setters that write back to the levels array
  const setLevelData = (patch) =>
    setLevels((prev) =>
      prev.map((l, i) => i === currentLevelIdx ? { ...l, ...patch } : l)
    );
  const setTiles = (newTiles) => setLevelData({ tiles: newTiles });
  const setEntities = (newEntities) => setLevelData({ entities: newEntities });
  const setDecorationTiles = (newDeco) => setLevelData({ decorationTiles: newDeco });
  const setOverlayTiles = (newOverlay) => setLevelData({ overlayTiles: newOverlay });

  const currentRows = tiles.length;
  const currentCols = map.cols ?? tiles[0]?.length ?? 0;

  // Base + decoration tile layers, drawn to a canvas below the entity-marker DOM layer.
  useEffect(() => {
    const canvas = boardCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const g = map.grid;

    // When isolating the active layer, only the base tile layer draws while
    // mode is "tiles" — decoration/overlay hide entirely instead of dimming.
    const hideBase = onlyShowCurrentLayer && mode !== "tiles";
    const hideDecoration = onlyShowCurrentLayer && mode !== "decoration";

    if (!hideBase) tiles.forEach((row, r) => {
      row.forEach((tile, c) => {
        if (!tile?.bgColor && !tile?.texture && !tile?.tileset) return;
        const dx = c * g, dy = r * g;
        drawTileCellRect(ctx, tile, dx, dy, g, g, editorImageCacheRef.current);
        if (tile.solid) {
          ctx.save();
          ctx.beginPath();
          ctx.rect(dx, dy, g, g);
          ctx.clip();
          ctx.strokeStyle = "rgba(255,255,255,0.3)";
          ctx.lineWidth = 2;
          for (let o = -g; o < g * 2; o += 6) {
            ctx.beginPath();
            ctx.moveTo(dx + o, dy);
            ctx.lineTo(dx + o + g, dy + g);
            ctx.stroke();
          }
          ctx.restore();
        }
      });
    });

    if (!hideDecoration) decorationTiles.forEach((row, r) => {
      row.forEach((tile, c) => {
        if (!tile?.bgColor && !tile?.texture && !tile?.tileset) return;
        const dx = c * g, dy = r * g;
        ctx.globalAlpha = (tile.opacity ?? 1) * (mode === "decoration" ? 1 : 0.75);
        drawTileCellRect(ctx, tile, dx, dy, g, g, editorImageCacheRef.current);
        ctx.globalAlpha = 1;
        if (mode === "decoration") {
          ctx.strokeStyle = "rgba(255,255,255,0.2)";
          ctx.lineWidth = 1;
          ctx.strokeRect(dx + 0.5, dy + 0.5, g - 1, g - 1);
        }
      });
    });
  }, [tiles, decorationTiles, map.grid, mode, onlyShowCurrentLayer]);

  // Overlay tile layer, drawn above the entity-marker DOM layer.
  useEffect(() => {
    const canvas = overlayCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const g = map.grid;

    if (onlyShowCurrentLayer && mode !== "overlay") return;

    overlayTiles.forEach((row, r) => {
      row.forEach((tile, c) => {
        if (!tile?.bgColor && !tile?.texture && !tile?.tileset) return;
        const dx = c * g, dy = r * g;
        const dim = mode === "overlay" ? 1 : 0.75;
        // Colour-fallback overlay tiles are extra-translucent, matching the legacy `${bgColor}cc` look
        const colorFade = tile.bgColor && !tile.tileset && !tile.texture ? 0.8 : 1;
        ctx.globalAlpha = (tile.opacity ?? 1) * dim * colorFade;
        drawTileCellRect(ctx, tile, dx, dy, g, g, editorImageCacheRef.current);
        ctx.globalAlpha = 1;
        if (mode === "overlay") {
          ctx.strokeStyle = "rgba(255,255,255,0.2)";
          ctx.lineWidth = 1;
          ctx.strokeRect(dx + 0.5, dy + 0.5, g - 1, g - 1);
        }
      });
    });
  }, [overlayTiles, map.grid, mode, onlyShowCurrentLayer]);

  const handleRowsChange = (newRows) => {
    newRows = Math.max(1, newRows);
    pushHistory();
    if (newRows > currentRows) {
      const added = Array.from({ length: newRows - currentRows }, () =>
        Array.from({ length: currentCols }, () => ({}))
      );
      setLevelData({
        tiles: [...tiles, ...added],
        decorationTiles: [...decorationTiles, ...added.map((r) => r.map(() => ({})))],
        overlayTiles: [...overlayTiles, ...added.map((r) => r.map(() => ({})))],
      });
    } else {
      setLevelData({
        tiles: tiles.slice(0, newRows),
        decorationTiles: decorationTiles.slice(0, newRows),
        overlayTiles: overlayTiles.slice(0, newRows),
      });
    }
  };

  const handleColsChange = (newCols) => {
    newCols = Math.max(1, newCols);
    pushHistory();
    const resizeTileRow = (row) =>
      newCols > row.length
        ? [...row, ...Array.from({ length: newCols - row.length }, () => ({}))]
        : row.slice(0, newCols);
    setLevelData({
      tiles: tiles.map(resizeTileRow),
      decorationTiles: decorationTiles.map(resizeTileRow),
      overlayTiles: overlayTiles.map(resizeTileRow),
    });
    setMap((m) => ({ ...m, cols: newCols }));
  };

  const addLevel = () => {
    const cols = currentCols || 10;
    const rows = currentRows || 10;
    const emptyTiles = blankTiles(rows, cols);
    pushHistory();
    setLevels((prev) => [
      ...prev,
      { name: `Level ${prev.length + 1}`, tiles: emptyTiles, entities: [], decorationTiles: blankTiles(rows, cols), overlayTiles: blankTiles(rows, cols) },
    ]);
    setCurrentLevelIdx(levels.length);
  };

  const removeLevel = (idx) => {
    if (levels.length <= 1) return;
    pushHistory();
    setLevels((prev) => prev.filter((_, i) => i !== idx));
    setCurrentLevelIdx((prev) => Math.min(prev, levels.length - 2));
  };

  const renameLevelAt = (idx, name) =>
    setLevels((prev) => prev.map((l, i) => (i === idx ? { ...l, name } : l)));

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [radius, setRadius] = useState(200);
  const [isGlobal, setIsGlobal] = useState(false);
  const [addressId, setAddressId] = useState("");
  const [hostAddresses, setHostAddresses] = useState([]);
  const [locationMode, setLocationMode] = useState("address"); // 'address' | 'coords'
  const [adminLat, setAdminLat] = useState("");
  const [adminLng, setAdminLng] = useState("");
  const [dungeonId, setDungeonId] = useState(null); // slug of the loaded dungeon, used for updates
  const [saveStatus, setSaveStatus] = useState(null); // null | 'saving' | 'saved' | 'error'
  const { showToast, toastElement } = useToast();
  const [showJson, setShowJson] = useState(false);
  const [jsonCopied, setJsonCopied] = useState(false);
  const [publishOpen, setPublishOpen] = useState(!id); // settings expanded for new dungeons

  // If editing an existing dungeon, load its data via slug.
  // Always pass a non-empty url (useGetQuery returns undefined when url is falsy),
  // and use enabled:false to prevent the actual fetch when there is no slug.
  const { data: existingData } =
    useGetQuery({
      url: isAdmin
        ? `/admin/dungeons/${id || "_"}`
        : `/host/dungeons/${id || "_"}`,
      enabled: !!id,
    }) ?? {};

  // Addresses — fetched once to populate the address picker
  useEffect(() => {
    const loader = isAdmin
      ? getAdminLocations({ limit: 200 })
      : getHostLocations();
    loader.then((data) => {
      const addresses = (data?.locations?.rows ?? []).flatMap((loc) =>
        (loc.addresses ?? []).map((addr) => ({
          ...addr,
          locationName: loc.name,
        }))
      );
      setHostAddresses(addresses);
      if (addresses.length && !addressId) setAddressId(addresses[0].id);
    });
  }, [null]);

  // NPC library — all DB NPCs available to place
  const { data: npcsData } = useGetQuery({
    url: isAdmin ? "/admin/npcs?limit=200" : "/host/npcs?limit=200",
    enabled: !!id,
  });
  const dbNpcs = npcsData?.npcs?.rows ?? [];

  // Existing NPC placements for the loaded dungeon
  const { data: placementsData, setPath: setPlacementsPath, setEnabled: setPlacementsEnabled } = useGetQuery({
    url: isAdmin
      ? `/admin/dungeons/${dungeonUUID || '_'}/npcs`
      : `/host/dungeons/${dungeonUUID || '_'}/npcs`,
    enabled: !!dungeonUUID,
  }) ?? {};
  useEffect(() => {
    if (!dungeonUUID) return;
    const url = isAdmin
      ? `/admin/dungeons/${dungeonUUID}/npcs`
      : `/host/dungeons/${dungeonUUID}/npcs`;
    setPlacementsPath?.(url);
    setPlacementsEnabled?.(true);
  }, [dungeonUUID]);

  useEffect(() => {
    if (placementsData) setNpcPlacements(placementsData.npc_placements ?? []);
  }, [placementsData]);

  useEffect(() => {
    if (!existingData?.dungeon) return;
    const d = existingData.dungeon;
    setName(d.name || "");
    setSlug(d.slug || "");
    setRadius(d.radius ?? 200);
    setIsGlobal(!!d.is_global);
    if (d.address_id) {
      setAddressId(d.address_id);
      setLocationMode("address");
    } else if (d.lat != null) {
      setAdminLat(String(d.lat));
      setAdminLng(String(d.lng ?? ""));
      setLocationMode("coords");
    }
    setDungeonId(d.slug || null);
    setDungeonUUID(d.id || null);
    if (d.map_data) {
      const md = d.map_data;
      setMap({ grid: md.grid || GRID_SIZE, cols: md.cols });
      // Strip any legacy inline NPC entities — placements now live in dungeon_npcs
      setLevels(
        tolevels(md).map((lv) => ({
          ...lv,
          entities: (lv.entities ?? [])
            .filter((e) => e.entityType !== "npc")
            .map((e) => e.entityType === "treasure" ? { ...e, entityType: "item" } : e),
        }))
      );
      setCurrentLevelIdx(0);
    } else {
      setMap({ grid: GRID_SIZE, cols: DEFAULT_COLS });
      setLevels([
        {
          name: "Level 1",
          tiles: blankTiles(DEFAULT_ROWS, DEFAULT_COLS),
          entities: [],
        },
      ]);
    }
  }, [existingData]);

  useEffect(() => {
    setLocal({ ...map, levels });
  }, [levels]);

  const handleSaveToDB = async () => {
    if (!name || !slug) {
      alert("Name and slug are required before saving.");
      return;
    }
    if (!isAdmin && !addressId) {
      alert("Please select an address before saving.");
      return;
    }
    setSaveStatus("saving");
    const mapPayload = { ...map, levels };

    try {
      if (isAdmin) {
        const base = { name, map_data: mapPayload, radius, is_global: isGlobal };
        if (locationMode === "address" && addressId) {
          base.address_id = addressId;
        } else if (locationMode === "coords") {
          if (adminLat) base.lat = adminLat;
          if (adminLng) base.lng = adminLng;
        }
        if (dungeonId) {
          await updateAdminDungeon(dungeonId, base);
        } else {
          await createAdminDungeon({ ...base, slug });
        }
      } else {
        if (dungeonId) {
          await updateDungeon(dungeonId, {
            name,
            map_data: mapPayload,
            radius,
            is_global: isGlobal,
            address_id: addressId,
          });
        } else {
          await createDungeon({
            name,
            slug,
            map_data: mapPayload,
            radius,
            is_global: isGlobal,
            address_id: addressId,
          });
        }
      }
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus(null), 3000);
    } catch {
      setSaveStatus("error");
    }
  };

  const makePairedLadder = (ladder, sourceLevelIdx) => ({
    id: ladder.pairedId,
    x: ladder.targetX ?? 40,
    y: ladder.targetY ?? 40,
    entityType: "ladder",
    backgroundColor: ladder.backgroundColor || "#c8a84b",
    direction: ladder.direction === "down" ? "up" : "down",
    targetLevel: sourceLevelIdx,
    targetX: ladder.x,
    targetY: ladder.y,
    pairedId: ladder.id,
  });

  const updateEntity = (id, changes) =>
    setEntities(entities.map((e) => (e.id === id ? { ...e, ...changes } : e)));

  const updateNpcPlacement = (id, changes) =>
    setNpcPlacements((prev) =>
      prev.map((p) => (p.id === id ? { ...p, ...changes } : p))
    );

  // Update placement in local state and persist to the API if the dungeon is saved
  const commitPlacementPatch = (placementId, patch) => {
    updateNpcPlacement(placementId, patch);
    if (dungeonUUIDRef.current)
      dungeonNpcUpdate(null, dungeonUUIDRef.current, placementId, patch);
  };

  // Interaction-area helpers — each NPC placement has a list of areas, each
  // with its own trigger tile, optional NPC walk-to spot, and its own dialogs.
  const updateArea = (p, areaIndex, patch) =>
    commitPlacementPatch(p.id, {
      interaction_areas: p.interaction_areas.map((a, i) =>
        i === areaIndex ? { ...a, ...patch } : a
      ),
    });

  const addArea = (p) =>
    commitPlacementPatch(p.id, {
      interaction_areas: [
        ...(p.interaction_areas || []),
        { id: crypto.randomUUID(), tile: null, npc_position: null, dialogs: [] },
      ],
    });

  const removeArea = (p, areaIndex) =>
    commitPlacementPatch(p.id, {
      interaction_areas: p.interaction_areas.filter((_, i) => i !== areaIndex),
    });

  // Shared brush props for the three PaintControls call sites
  const brush = {
    tileMode,
    setTileMode,
    color,
    setColor,
    isPickingColor,
    onStartPick: () => setIsPickingColor(true),
    selectedBrush,
    setSelectedBrush,
    patternSize,
    setPatternSize,
    floodFill,
    setFloodFill,
    flipX,
    setFlipX,
    copyMode,
    setCopyMode,
    clipboard,
  };

  // Sidebar picker for one pickable tile on one interaction area of an NPC placement
  const renderAreaTilePicker = (p, areaIndex, key, label, hint) => {
    const value = p.interaction_areas[areaIndex][key];
    const isPicking =
      pickingTileFor?.id === p.id &&
      pickingTileFor?.areaIndex === areaIndex &&
      pickingTileFor?.key === key;
    const togglePick = () =>
      setPickingTileFor(isPicking ? null : { id: p.id, areaIndex, key });
    return (
      <div>
        <SidebarLabel>{label}</SidebarLabel>
        <div
          style={{ fontSize: "0.58rem", color: c.dim, marginBottom: "0.3rem" }}
        >
          {hint}
        </div>
        {value ? (
          <div style={{ marginBottom: "0.5rem" }}>
            <div
              style={{
                fontSize: "0.62rem",
                color: c.muted,
                marginBottom: "0.3rem",
              }}
            >
              Tile ({value.x}, {value.y})
            </div>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <span
                style={{ fontSize: "0.6rem", color: c.muted, cursor: "pointer" }}
                onClick={togglePick}
              >
                Change tile
              </span>
              <span
                style={{ fontSize: "0.6rem", color: c.danger, cursor: "pointer" }}
                onClick={() => updateArea(p, areaIndex, { [key]: null })}
              >
                Remove
              </span>
            </div>
          </div>
        ) : (
          <span
            style={{
              fontSize: "0.62rem",
              color: isPicking ? colors("accent") : c.muted,
              cursor: "pointer",
              display: "block",
              marginBottom: "0.5rem",
            }}
            onClick={togglePick}
          >
            {isPicking ? "↖ click a tile on the map…" : "+ Pick tile"}
          </span>
        )}
      </div>
    );
  };

  const getClientXY = (e) => {
    const t = e.touches?.[0] ?? e.changedTouches?.[0];
    return t ? { clientX: t.clientX, clientY: t.clientY } : { clientX: e.clientX, clientY: e.clientY };
  };

  const getTileCoords = (e) => {
    if (!boardRef.current) return null;
    const rect = boardRef.current.getBoundingClientRect();
    const { clientX, clientY } = getClientXY(e);
    const col = Math.max(
      0,
      Math.min(Math.floor((clientX - rect.left) / map.grid), currentCols - 1)
    );
    const row = Math.max(
      0,
      Math.min(Math.floor((clientY - rect.top) / map.grid), tiles.length - 1)
    );
    return { row, col };
  };

  // The tiles/decorationTiles/overlayTiles array (and its setter) for whichever layer
  // `mode` currently points at — used anywhere logic needs to act on "the active layer"
  // generically (stamp hit-testing, drag/erase/move, flood fill).
  const getLayerArray = () => (mode === "decoration" ? decorationTiles : mode === "overlay" ? overlayTiles : tiles);
  const setLayerArray = () => (mode === "decoration" ? setDecorationTiles : mode === "overlay" ? setOverlayTiles : setTiles);

  // If (row,col) on the active layer belongs to a placed stamp, returns its footprint.
  const findStampAt = (row, col) => {
    const arr = getLayerArray();
    const cell = arr[row]?.[col];
    if (!cell?.stamp || !cell.stampOrigin) return null;
    const stamp = getStamp(cell.stamp);
    if (!stamp) return null;
    return { stampId: cell.stamp, originCol: cell.stampOrigin[0], originRow: cell.stampOrigin[1], w: stamp.w, h: stamp.h };
  };

  // Explodes a multi-cell stamp into individual TileCells on the active layer,
  // anchored at `start`. Clipped/rejected if it would run off the map edge.
  const placeStampAt = (start) => {
    const stamp = getStamp(selectedBrush.stampId);
    if (!stamp) return;
    const arr = mode === "decoration" ? decorationTiles : mode === "overlay" ? overlayTiles : tiles;
    const setArr = mode === "decoration" ? setDecorationTiles : mode === "overlay" ? setOverlayTiles : setTiles;
    if (start.row + stamp.h > arr.length || start.col + stamp.w > currentCols) return;
    pushHistory();

    const newRows = arr.map((r) => [...r]);
    for (let dy = 0; dy < stamp.h; dy++) {
      const r = start.row + dy;
      for (let dx = 0; dx < stamp.w; dx++) {
        const c = start.col + dx;
        // Mirroring the whole object also reverses which column each source tile comes from.
        const sx = flipX ? stamp.w - 1 - dx : dx;
        while (newRows[r].length <= c) newRows[r].push({});
        newRows[r][c] = {
          tileset: stamp.sheet,
          tx: stamp.tx + sx,
          ty: stamp.ty + dy,
          solid: stamp.solid,
          stamp: stamp.id,
          stampOrigin: [start.col, start.row],
          ...(flipX ? { flipX: true } : {}),
        };
      }
    }
    setArr(newRows);
  };

  // Captures the dragged rectangle from the active layer into the clipboard, stripping
  // stamp identity (a paste is a flat copy, not a linked instance of the source stamp),
  // then switches the brush to 'clipboard' so the very next click pastes it.
  const copyRegionToClipboard = (start, end) => {
    const arr = getLayerArray();
    const minRow = Math.min(start.row, end.row);
    const maxRow = Math.max(start.row, end.row);
    const minCol = Math.min(start.col, end.col);
    const maxCol = Math.max(start.col, end.col);
    const cells = [];
    for (let r = minRow; r <= maxRow; r++) {
      const row = [];
      for (let c = minCol; c <= maxCol; c++) {
        const { stamp, stampOrigin, ...clean } = arr[r]?.[c] ?? {};
        row.push(clean);
      }
      cells.push(row);
    }
    setClipboard({ w: maxCol - minCol + 1, h: maxRow - minRow + 1, cells });
    setCopyMode(false);
    setSelectedBrush({ kind: "clipboard" });
    setTileMode("tileset"); // so the paste status/preview shows regardless of prior brush mode
  };

  const pasteClipboardAt = (start) => {
    if (!clipboard) return;
    const arr = getLayerArray();
    const setArr = setLayerArray();
    const { w, h, cells } = clipboard;
    if (start.row + h > arr.length || start.col + w > currentCols) return;
    pushHistory();
    const newRows = arr.map((r) => [...r]);
    for (let dy = 0; dy < h; dy++) {
      const r = start.row + dy;
      for (let dx = 0; dx < w; dx++) {
        const c = start.col + dx;
        while (newRows[r].length <= c) newRows[r].push({});
        newRows[r][c] = { ...cells[dy][dx] };
      }
    }
    setArr(newRows);
  };

  const fillArea = (start, end) => {
    if (copyMode) {
      copyRegionToClipboard(start, end);
      return;
    }
    if (selectedBrush.kind === "stamp") {
      placeStampAt(start);
      return;
    }
    if (selectedBrush.kind === "clipboard") {
      pasteClipboardAt(start);
      return;
    }
    pushHistory();

    const minRow = Math.min(start.row, end.row);
    const maxRow = Math.max(start.row, end.row);
    const minCol = Math.min(start.col, end.col);
    const maxCol = Math.max(start.col, end.col);

    // Value for one painted cell on the active layer, given its grid position and any
    // layer-specific extra fields. Flat tileset brushes with a >1x1 patternSize sample a
    // w x h patch of the sheet, anchored so the picked tile always lands at the top-left
    // corner of the painted rectangle (minRow/minCol), then advances in reading order.
    const paintValue = (r, c, extra) => {
      if (tileMode !== "tileset") return { bgColor: color, ...extra };
      const sheet = getSheet(selectedBrush.sheet);
      const pw = Math.max(1, patternSize.w);
      const ph = Math.max(1, patternSize.h);
      const dx = (c - minCol) % pw;
      const dy = (r - minRow) % ph;
      const tx = sheet ? (selectedBrush.tx + dx) % sheet.cols : selectedBrush.tx;
      const ty = sheet ? (selectedBrush.ty + dy) % sheet.rows : selectedBrush.ty;
      return { tileset: selectedBrush.sheet, tx, ty, ...(flipX ? { flipX: true } : {}), ...extra };
    };

    if (mode === "decoration") {
      const newRows = decorationTiles.map((r) => [...r]);
      for (let r = minRow; r <= maxRow; r++) {
        if (!newRows[r]) continue;
        for (let c = minCol; c <= maxCol; c++) {
          while (newRows[r].length <= c) newRows[r].push({});
          newRows[r][c] = decorationErase ? {} : paintValue(r, c, { opacity: decorationOpacity });
        }
      }
      setDecorationTiles(newRows);
      return;
    }

    if (mode === "overlay") {
      const newRows = overlayTiles.map((r) => [...r]);
      for (let r = minRow; r <= maxRow; r++) {
        if (!newRows[r]) continue;
        for (let c = minCol; c <= maxCol; c++) {
          while (newRows[r].length <= c) newRows[r].push({});
          newRows[r][c] = overlayErase ? {} : paintValue(r, c, { opacity: overlayOpacity });
        }
      }
      setOverlayTiles(newRows);
      return;
    }

    const newRows = tiles.map((r) => [...r]);
    for (let r = minRow; r <= maxRow; r++) {
      if (!newRows[r]) continue;
      for (let c = minCol; c <= maxCol; c++) {
        while (newRows[r].length <= c) newRows[r].push({});
        newRows[r][c] = paintValue(r, c, { solid });
      }
    }
    setTiles(newRows);
  };

  // Two TileCells are "the same material" for flood-fill matching purposes if they're
  // both the same sheet slice, both the same flat color, or both empty. Stamp cells only
  // match other cells of the exact same stamp placement (stampOrigin included), so a fill
  // never eats into or spreads out of a placed piece of furniture.
  const cellsMatch = (a, b) => {
    const av = a || {};
    const bv = b || {};
    if (av.stamp || bv.stamp) return av.stamp === bv.stamp && String(av.stampOrigin) === String(bv.stampOrigin);
    if (av.tileset || bv.tileset) return av.tileset === bv.tileset && av.tx === bv.tx && av.ty === bv.ty;
    if (av.bgColor || bv.bgColor) return av.bgColor === bv.bgColor;
    return true; // both empty
  };

  // 4-connected flood fill of the region matching the clicked cell's current value,
  // starting at `start`, using the same brush/pattern logic as a normal paint stroke.
  const floodFillAt = (start) => {
    const arr = getLayerArray();
    const setArr = setLayerArray();
    const target = arr[start.row]?.[start.col] ?? {};
    const erase = mode === "decoration" ? decorationErase : mode === "overlay" ? overlayErase : false;

    const paintValue = (r, c) => {
      if (erase) return {};
      const extra = mode === "decoration" ? { opacity: decorationOpacity } : mode === "overlay" ? { opacity: overlayOpacity } : { solid };
      if (tileMode !== "tileset") return { bgColor: color, ...extra };
      const sheet = getSheet(selectedBrush.sheet);
      const pw = Math.max(1, patternSize.w);
      const ph = Math.max(1, patternSize.h);
      const dx = (((c - start.col) % pw) + pw) % pw;
      const dy = (((r - start.row) % ph) + ph) % ph;
      const tx = sheet ? (selectedBrush.tx + dx) % sheet.cols : selectedBrush.tx;
      const ty = sheet ? (selectedBrush.ty + dy) % sheet.rows : selectedBrush.ty;
      return { tileset: selectedBrush.sheet, tx, ty, ...(flipX ? { flipX: true } : {}), ...extra };
    };

    if (cellsMatch(target, paintValue(start.row, start.col))) return; // already this value, nothing to do

    pushHistory();
    const newRows = arr.map((r) => [...r]);
    const rows = newRows.length;
    const cols = currentCols;
    const visited = new Set();
    const stack = [[start.row, start.col]];
    while (stack.length) {
      const [r, c] = stack.pop();
      const key = `${r},${c}`;
      if (visited.has(key) || r < 0 || r >= rows || c < 0 || c >= cols || !newRows[r]) continue;
      visited.add(key);
      if (!cellsMatch(newRows[r][c], target)) continue;
      while (newRows[r].length <= c) newRows[r].push({});
      newRows[r][c] = paintValue(r, c);
      stack.push([r - 1, c], [r + 1, c], [r, c - 1], [r, c + 1]);
    }
    setArr(newRows);
  };

  const handleBoardMouseDown = (e) => {
    if (isPickingColor) {
      e.preventDefault();
      const coords = getTileCoords(e);
      if (coords) {
        const sourceTiles = mode === "overlay" ? overlayTiles : mode === "decoration" ? decorationTiles : tiles;
        const tile = sourceTiles[coords.row]?.[coords.col];
        if (tile?.bgColor) setColor(tile.bgColor);
      }
      setIsPickingColor(false);
      return;
    }

    if (mode === "tiles" || mode === "decoration" || mode === "overlay") {
      e.preventDefault();
      const coords = getTileCoords(e);
      if (!coords) return;

      const hitStamp = !copyMode && findStampAt(coords.row, coords.col);
      if (hitStamp) {
        const { clientX, clientY } = getClientXY(e);
        stampDragRef.current = { ...hitStamp, startClientX: clientX, startClientY: clientY, hasMoved: false };
        setStampDragPreview({ col: hitStamp.originCol, row: hitStamp.originRow, w: hitStamp.w, h: hitStamp.h });
        return;
      }

      if (floodFill && !copyMode && selectedBrush.kind !== "stamp") {
        floodFillAt(coords);
        return;
      }

      setDragStart(coords);
      setDragEnd(coords);
      return;
    }

    if (!boardRef.current) return;
    const rect = boardRef.current.getBoundingClientRect();
    const { clientX: downX, clientY: downY } = getClientXY(e);
    const gx = Math.floor((downX - rect.left) / map.grid) * map.grid;
    const gy = Math.floor((downY - rect.top) / map.grid) * map.grid;
    const entityType = ENTITY_DEFAULTS[mode]?.entityType;

    // NPC placements are DB-backed — hit-detect against npcPlacements, not entities
    if (mode === "npcs") {
      const hit = npcPlacementsRef.current.find(
        (p) => p.level_index === currentLevelIdx && p.x === gx && p.y === gy
      );
      if (hit) {
        e.preventDefault();
        entityDragRef.current = {
          id: hit.id,
          isNpcPlacement: true,
          startClientX: downX,
          startClientY: downY,
          hasMoved: false,
        };
        setDragEntityPreview({
          entity: {
            ...hit,
            backgroundColor: hit.background_color || "#4488cc",
          },
          x: gx,
          y: gy,
        });
        return;
      }
      if (editingNpcId && !pickingTileFor) {
        e.preventDefault();
        const coords = getTileCoords(e);
        if (!coords) return;
        boundaryDrawRef.current = {
          entityId: editingNpcId,
          isNpcPlacement: true,
        };
        setBoundaryDrawPreview({ startTile: coords, endTile: coords });
      }
      return;
    }

    // Entity drag — check for an entity at the clicked cell first
    const hit = entities.find(
      (en) => en.entityType === entityType && en.x === gx && en.y === gy
    );
    if (hit) {
      e.preventDefault();
      entityDragRef.current = {
        id: hit.id,
        startClientX: downX,
        startClientY: downY,
        hasMoved: false,
      };
      setDragEntityPreview({ entity: hit, x: gx, y: gy });
      return;
    }

    // Boundary draw (monsters only — NPC case handled above)
    const editingId = mode === "monsters" ? editingMonsterId : null;
    if (editingId && !pickingTileFor) {
      e.preventDefault();
      const coords = getTileCoords(e);
      if (!coords) return;
      boundaryDrawRef.current = { entityId: editingId };
      setBoundaryDrawPreview({ startTile: coords, endTile: coords });
    }
  };

  const handleBoardMouseMove = (e) => {
    const { clientX: moveX, clientY: moveY } = getClientXY(e);
    // Boundary corner resize
    if (boundaryResizeRef.current && boardRef.current) {
      const { entityId, anchorTileX, anchorTileY, isNpcPlacement } =
        boundaryResizeRef.current;
      const rect = boardRef.current.getBoundingClientRect();
      const curCol = Math.round((moveX - rect.left) / map.grid);
      const curRow = Math.round((moveY - rect.top) / map.grid);
      const x = Math.min(anchorTileX, curCol);
      const y = Math.min(anchorTileY, curRow);
      const w = Math.max(1, Math.abs(curCol - anchorTileX));
      const h = Math.max(1, Math.abs(curRow - anchorTileY));
      if (isNpcPlacement) {
        updateNpcPlacement(entityId, { boundary: { x, y, w, h } });
      } else {
        updateEntity(entityId, { boundary: { x, y, w, h } });
      }
      return;
    }

    // Stamp drag (select/move) in progress
    if (stampDragRef.current && boardRef.current) {
      const drag = stampDragRef.current;
      const dx = moveX - drag.startClientX;
      const dy = moveY - drag.startClientY;
      if (!drag.hasMoved && Math.hypot(dx, dy) < 4) return;
      drag.hasMoved = true;
      const rect = boardRef.current.getBoundingClientRect();
      const col = Math.max(0, Math.min(Math.floor((moveX - rect.left) / map.grid), currentCols - drag.w));
      const row = Math.max(0, Math.min(Math.floor((moveY - rect.top) / map.grid), tiles.length - drag.h));
      setStampDragPreview({ col, row, w: drag.w, h: drag.h });
      return;
    }

    // Boundary draw preview
    if (boundaryDrawRef.current) {
      const coords = getTileCoords(e);
      if (coords)
        setBoundaryDrawPreview((prev) =>
          prev ? { ...prev, endTile: coords } : prev
        );
      return;
    }

    if (dragStart && (mode === "tiles" || mode === "decoration" || mode === "overlay")) {
      const coords = getTileCoords(e);
      if (coords) setDragEnd(coords);
      return;
    }

    if (entityDragRef.current && boardRef.current) {
      const dx = moveX - entityDragRef.current.startClientX;
      const dy = moveY - entityDragRef.current.startClientY;
      if (!entityDragRef.current.hasMoved && Math.hypot(dx, dy) < 4) return;

      entityDragRef.current.hasMoved = true;
      const rect = boardRef.current.getBoundingClientRect();
      const gx =
        Math.max(0, Math.floor((moveX - rect.left) / map.grid)) * map.grid;
      const gy =
        Math.max(0, Math.floor((moveY - rect.top) / map.grid)) * map.grid;
      setDragEntityPreview((prev) => (prev ? { ...prev, x: gx, y: gy } : prev));
      return;
    }

    // Nothing else is being dragged — show what's under the cursor.
    if (mode === "tiles" || mode === "decoration" || mode === "overlay") {
      const coords = getTileCoords(e);
      if (!coords) {
        setHoverInfo(null);
        setPlacementPreview(null);
        return;
      }
      const cell = getLayerArray()[coords.row]?.[coords.col];
      let text = "empty";
      if (cell?.stamp) {
        text = getStamp(cell.stamp)?.label ?? cell.stamp;
      } else if (cell?.tileset) {
        text = `${getSheet(cell.tileset)?.label ?? cell.tileset} (${cell.tx},${cell.ty})`;
      } else if (cell?.bgColor) {
        text = cell.bgColor;
      }
      if (cell?.solid) text += " · solid";
      setHoverInfo({ clientX: moveX, clientY: moveY, text });

      // Show the footprint outline at the cursor before a stamp/clipboard is placed.
      const footprint =
        !copyMode && selectedBrush.kind === "stamp"
          ? getStamp(selectedBrush.stampId)
          : !copyMode && selectedBrush.kind === "clipboard"
            ? clipboard
            : null;
      if (footprint) {
        const col = Math.min(coords.col, currentCols - footprint.w);
        const row = Math.min(coords.row, tiles.length - footprint.h);
        setPlacementPreview({ col, row, w: footprint.w, h: footprint.h });
      } else {
        setPlacementPreview(null);
      }
    } else {
      setHoverInfo(null);
      setPlacementPreview(null);
    }
  };

  // completeDragRef always holds a fresh closure so the global mouseup
  // handler never sees stale state even if the mouse releases outside the board.
  const completeDragRef = useRef(null);
  completeDragRef.current = () => {
    // Stamp select/erase/move — release without moving erases it, release after
    // dragging moves it (clipped to bounds by the move-preview in handleBoardMouseMove).
    if (stampDragRef.current) {
      const { stampId, originCol, originRow, w, h, hasMoved } = stampDragRef.current;
      const target = stampDragPreview;
      stampDragRef.current = null;
      setStampDragPreview(null);

      const arr = getLayerArray();
      const setArr = setLayerArray();
      const placementFlipX = !!arr[originRow]?.[originCol]?.flipX; // preserve the placement's own flip, not the current brush
      pushHistory();
      const newRows = arr.map((r) => [...r]);
      for (let dy = 0; dy < h; dy++) {
        const r = originRow + dy;
        if (!newRows[r]) continue;
        for (let dx = 0; dx < w; dx++) newRows[r][originCol + dx] = {};
      }
      if (hasMoved && target) {
        const stamp = getStamp(stampId);
        for (let dy = 0; dy < h; dy++) {
          const r = target.row + dy;
          if (!newRows[r]) continue;
          for (let dx = 0; dx < w; dx++) {
            const c = target.col + dx;
            const sx = placementFlipX ? w - 1 - dx : dx;
            while (newRows[r].length <= c) newRows[r].push({});
            newRows[r][c] = {
              tileset: stamp.sheet,
              tx: stamp.tx + sx,
              ty: stamp.ty + dy,
              solid: stamp.solid,
              stamp: stamp.id,
              stampOrigin: [target.col, target.row],
              ...(placementFlipX ? { flipX: true } : {}),
            };
          }
        }
      }
      setArr(newRows);
      return;
    }

    // Boundary resize end
    if (boundaryResizeRef.current) {
      boundaryResizeRef.current = null;
      return;
    }

    // Boundary draw commit
    if (boundaryDrawRef.current && boundaryDrawPreview) {
      const { entityId, isNpcPlacement } = boundaryDrawRef.current;
      const { startTile, endTile } = boundaryDrawPreview;
      const x = Math.min(startTile.col, endTile.col);
      const y = Math.min(startTile.row, endTile.row);
      const w = Math.max(1, Math.abs(endTile.col - startTile.col) + 1);
      const h = Math.max(1, Math.abs(endTile.row - startTile.row) + 1);
      const boundary = { x, y, w, h };
      if (isNpcPlacement) {
        commitPlacementPatch(entityId, { boundary });
      } else {
        updateEntity(entityId, { boundary });
      }
      boundaryDrawRef.current = null;
      setBoundaryDrawPreview(null);
      boundaryDrawInitiatedRef.current = true;
      setTimeout(() => {
        boundaryDrawInitiatedRef.current = false;
      }, 0);
      return;
    }
    if (boundaryDrawRef.current) {
      boundaryDrawRef.current = null;
      setBoundaryDrawPreview(null);
      return;
    }

    // Tile / overlay drag
    if (dragStart) {
      fillArea(dragStart, dragEnd || dragStart);
      setDragStart(null);
      setDragEnd(null);
      return;
    }

    // Entity drag
    if (entityDragRef.current) {
      const { id, isNpcPlacement, hasMoved } = entityDragRef.current;
      entityDragRef.current = null;

      if (hasMoved && dragEntityPreview) {
        const { x, y } = dragEntityPreview;
        if (isNpcPlacement) {
          commitPlacementPatch(id, { x, y });
        } else {
          pushHistory();
          setEntities(
            entities.map((en) => (en.id === id ? { ...en, x, y } : en))
          );
        }
        entityDragInitiatedRef.current = true;
        setTimeout(() => {
          entityDragInitiatedRef.current = false;
        }, 0);
      }

      setDragEntityPreview(null);
    }
  };

  useEffect(() => {
    const handler = () => completeDragRef.current();
    document.addEventListener("pointerup", handler);
    return () => {
      document.removeEventListener("pointerup", handler);
    };
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") {
        setPickingTileFor(null);
        setIsPickingColor(false);
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        if (historyRef.current.length === 0) return;
        const prev = historyRef.current[historyRef.current.length - 1];
        historyRef.current = historyRef.current.slice(0, -1);
        setLevels(prev);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const handleEntityClick = (e, row) => {
    if (mode === "tiles" || mode === "decoration" || mode === "overlay") return;
    if (entityDragInitiatedRef.current) return;
    if (boundaryDrawInitiatedRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const col = Math.floor((e.clientX - rect.left) / map.grid);
    const x = col * map.grid;
    const y = row * map.grid;

    // Tile pick mode — set the picked tile/position on the NPC placement's area
    if (pickingTileFor) {
      const placement = npcPlacementsRef.current.find(
        (p) => p.id === pickingTileFor.id
      );
      if (placement) {
        const newAreas = (placement.interaction_areas || []).map((a, i) =>
          i === pickingTileFor.areaIndex
            ? { ...a, [pickingTileFor.key]: { x: col, y: row } }
            : a
        );
        commitPlacementPatch(pickingTileFor.id, { interaction_areas: newAreas });
      }
      setPickingTileFor(null);
      return;
    }

    const entityType = ENTITY_DEFAULTS[mode]?.entityType;

    if (mode === "npcs") {
      // If clicking an existing placement, toggle its expand panel
      const existing = npcPlacementsRef.current.find(
        (p) => p.level_index === currentLevelIdx && p.x === x && p.y === y
      );
      if (existing) {
        setEditingNpcId(editingNpcId === existing.id ? null : existing.id);
        return;
      }

      // Require a saved dungeon and a selected NPC from the library to place a new one
      if (!dungeonUUIDRef.current || !selectedNpcId) return;

      // Optimistically create placement, confirm with API
      const npc = dbNpcs.find((n) => n.id === selectedNpcId);
      const tempId = `temp_${Date.now()}`;
      setNpcPlacements((prev) => [
        ...prev,
        {
          id: tempId,
          npc_id: selectedNpcId,
          level_index: currentLevelIdx,
          x,
          y,
          width: GRID_SIZE,
          height: GRID_SIZE,
          boundary: null,
          interaction_areas: [],
          background_color: npc?.background_color || "#4488cc",
          Npc: npc,
        },
      ]);
      dungeonNpcCreate(null, dungeonUUIDRef.current, {
        npc_id: selectedNpcId,
        level_index: currentLevelIdx,
        x,
        y,
      }).then((res) => {
        const placement = res?.npc_placement;
        if (placement) {
          setNpcPlacements((prev) =>
            prev.map((p) => (p.id === tempId ? { ...placement, Npc: npc } : p))
          );
        } else {
          setNpcPlacements((prev) => prev.filter((p) => p.id !== tempId));
        }
      }).catch((err) => {
        setNpcPlacements((prev) => prev.filter((p) => p.id !== tempId));
        showToast(err?.message || "Failed to place NPC");
      });
      return;
    }

    // Toggle: remove if exists at this spot, else add (non-NPC entities)
    const existing = entities.findIndex(
      (en) => en.entityType === entityType && en.x === x && en.y === y
    );
    pushHistory();
    if (existing > -1) {
      const removed = entities[existing];
      if (removed.entityType === "ladder" && removed.pairedId) {
        const targetLvl = removed.targetLevel ?? 0;
        setLevels((prev) =>
          prev.map((l, i) => {
            if (i === currentLevelIdx)
              return { ...l, entities: (l.entities || []).filter((_, idx) => idx !== existing) };
            if (i === targetLvl)
              return { ...l, entities: (l.entities || []).filter((e) => e.id !== removed.pairedId) };
            return l;
          })
        );
      } else {
        setEntities(entities.filter((_, i) => i !== existing));
      }
    } else {
      const id = `${entityType}_${Date.now()}`;
      const typeOverride = mode === "items"
        ? { treasureType: selectedItemType.value, backgroundColor: selectedItemType.color }
        : {};
      const newEntity = { id, x, y, ...ENTITY_DEFAULTS[mode], ...typeOverride };
      if (entityType === "ladder") {
        const pairedId = `${entityType}_${Date.now() + 1}`;
        // Smart defaults: going down from level 0, going up from any sub-level
        const direction = currentLevelIdx > 0 ? "up" : "down";
        const targetLevel = currentLevelIdx > 0 ? currentLevelIdx - 1 : 1;
        const newLadder = { ...newEntity, pairedId, direction, targetLevel };
        const targetLvl = newLadder.targetLevel;
        const paired = makePairedLadder(newLadder, currentLevelIdx);
        setLevels((prev) =>
          prev.map((l, i) => {
            if (i === currentLevelIdx && i === targetLvl)
              return { ...l, entities: [...(l.entities || []), newLadder, paired] };
            if (i === currentLevelIdx)
              return { ...l, entities: [...(l.entities || []), newLadder] };
            if (i === targetLvl)
              return { ...l, entities: [...(l.entities || []), paired] };
            return l;
          })
        );
      } else {
        setEntities([...entities, newEntity]);
      }
    }
  };

  const levelNpcs = npcPlacements.filter(
    (p) => p.level_index === currentLevelIdx
  );

  const entitiesInRow = (row) =>
    entities.filter(
      (e) => e.entityType !== "npc" && Math.floor(e.y / map.grid) === row
    );

  const npcPlacementsInRow = (r) =>
    levelNpcs.filter((p) => Math.floor(p.y / map.grid) === r);

  // Raw map_data payload — same shape sent to the API on save (see handleSaveToDB)
  const mapDataJson = useMemo(
    () => JSON.stringify({ ...map, levels }, null, 2),
    [map, levels]
  );

  // Editable draft of the JSON panel — separate from the live-derived
  // `mapDataJson` so typing doesn't fight re-renders from state changes.
  const [jsonDraft, setJsonDraft] = useState(mapDataJson);
  const [jsonError, setJsonError] = useState(null);

  const copyMapDataJson = () => {
    navigator.clipboard?.writeText(jsonDraft).then(() => {
      setJsonCopied(true);
      setTimeout(() => setJsonCopied(false), 1500);
    });
  };

  // Re-sync the editable draft to the live data whenever the panel is opened
  // (not on every levels/map change — that would fight the user's typing).
  useEffect(() => {
    if (showJson) setJsonDraft(mapDataJson);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showJson]);

  const jsonDirty = showJson && jsonDraft !== mapDataJson;

  const applyJsonEdits = () => {
    let parsed;
    try {
      parsed = JSON.parse(jsonDraft);
    } catch (e) {
      setJsonError(e.message);
      return;
    }
    if (!parsed || typeof parsed !== "object" || !Array.isArray(parsed.levels)) {
      setJsonError("Must be an object with a `levels` array");
      return;
    }
    const { levels: newLevels, ...newMap } = parsed;
    pushHistory();
    setMap(newMap);
    setLevels(newLevels);
    setCurrentLevelIdx((i) => Math.min(i, newLevels.length - 1));
    setJsonError(null);
  };

  const revertJsonEdits = () => {
    setJsonDraft(mapDataJson);
    setJsonError(null);
  };

  return (
    <Container>
      {toastElement}
      <h2 style={{ color: c.text, marginTop: 0 }}>Dungeon Map Editor</h2>

      <Toolbar>
        <ToolbarGroup>
          {MODES.map((m) => (
            <ModeButton key={m} $active={mode === m} onClick={() => setMode(m)}>
              {m}
            </ModeButton>
          ))}
        </ToolbarGroup>

        <ToolbarDivider />

        <ModeButton
          $active={panMode}
          onClick={() => setPanMode((v) => !v)}
          title="Toggle pan/paint mode for touch screens"
        >
          {panMode ? "✋ Pan" : "✏️ Paint"}
        </ModeButton>

        <ModeButton
          $active={showJson}
          onClick={() => setShowJson((v) => !v)}
          title="View the raw map_data JSON"
        >
          {"{ } JSON"}
        </ModeButton>

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.35rem",
            fontFamily: "Silkscreen, monospace",
            fontSize: "0.62rem",
            color: c.muted,
            cursor: "pointer",
          }}
          title="Hide the other tile layers while editing tiles, decoration, or overlay"
        >
          <input
            type="checkbox"
            checked={onlyShowCurrentLayer}
            onChange={(e) => setOnlyShowCurrentLayer(e.target.checked)}
          />
          Only show current layer
        </label>

        <ToolbarGroup style={{ marginLeft: "auto" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.4rem",
              fontSize: "0.65rem",
              color: c.muted,
            }}
          >
            <label>Rows</label>
            <input
              type="number"
              min={1}
              value={currentRows}
              onChange={(e) => handleRowsChange(Number(e.target.value))}
              style={{ width: 52, ...smallInput }}
            />
            <label>Cols</label>
            <input
              type="number"
              min={1}
              value={currentCols}
              onChange={(e) => handleColsChange(Number(e.target.value))}
              style={{ width: 52, ...smallInput }}
            />
          </div>

          <ModeButton
            onClick={() => {
              if (
                window.confirm(
                  "Overwrite current level with a procedurally generated dungeon?"
                )
              ) {
                const generated = generateDungeon({
                  cols: currentCols,
                  rows: currentRows,
                });
                setTiles(generated);
              }
            }}
          >
            Generate
          </ModeButton>

          <ModeButton
            style={{ color: c.danger, borderColor: c.danger }}
            onClick={() => {
              if (window.confirm("Reset map to blank?")) {
                setMap({ grid: GRID_SIZE, cols: DEFAULT_COLS });
                setLevels([
                  {
                    name: "Level 1",
                    tiles: blankTiles(DEFAULT_ROWS, DEFAULT_COLS),
                    entities: [],
                  },
                ]);
                setCurrentLevelIdx(0);
                localStorage.removeItem(STORAGE_KEY);
              }
            }}
          >
            Reset
          </ModeButton>
        </ToolbarGroup>
      </Toolbar>

      {showJson && (
        <DockablePanel id="json-view" title="Map Data (JSON)" edge="right">
          <div
            style={{
              border: `1px solid ${c.border}`,
              background: c.bg,
              padding: "0.5rem",
              width: 480,
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "0.4rem",
                marginBottom: "0.4rem",
              }}
            >
              <span style={{ fontSize: "0.6rem", color: c.muted }}>
                {jsonError ? "" : jsonDirty ? "Unsaved edits" : ""}
              </span>
              <div style={{ display: "flex", gap: "0.4rem" }}>
                <ModeButton
                  onClick={revertJsonEdits}
                  disabled={!jsonDirty}
                  style={!jsonDirty ? { opacity: 0.4, cursor: "default" } : undefined}
                >
                  Revert
                </ModeButton>
                <ModeButton onClick={applyJsonEdits}>
                  Apply
                </ModeButton>
                <ModeButton onClick={copyMapDataJson}>
                  {jsonCopied ? "Copied!" : "Copy"}
                </ModeButton>
              </div>
            </div>
            {jsonError && (
              <div
                style={{
                  color: c.danger,
                  fontSize: "0.6rem",
                  marginBottom: "0.4rem",
                }}
              >
                {jsonError}
              </div>
            )}
            <textarea
              value={jsonDraft}
              onChange={(e) => {
                setJsonDraft(e.target.value);
                if (jsonError) setJsonError(null);
              }}
              spellCheck={false}
              style={{
                width: "100%",
                height: "70vh",
                resize: "vertical",
                boxSizing: "border-box",
                background: c.bg,
                border: `1px solid ${jsonError ? c.danger : c.border}`,
                color: c.text,
                fontFamily: "'Silkscreen', monospace",
                fontSize: "0.62rem",
                lineHeight: 1.4,
                padding: "0.4rem",
              }}
            />
          </div>
        </DockablePanel>
      )}

      {/* ── Level tabs ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.25rem",
          marginBottom: "0.5rem",
          flexWrap: "wrap",
        }}
      >
        {levels.map((lv, idx) => (
          <div key={idx} style={{ display: "flex", alignItems: "center" }}>
            {idx === currentLevelIdx ? (
              <div style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                <input
                  value={lv.name}
                  onChange={(ev) => renameLevelAt(idx, ev.target.value)}
                  style={{ ...smallInput, minWidth: 70, maxWidth: 120 }}
                  onClick={(ev) => ev.stopPropagation()}
                />
                <select
                  value={lv.visibility ?? "bright"}
                  title="Level visibility"
                  onChange={(ev) =>
                    setLevels((prev) =>
                      prev.map((l, i) =>
                        i === idx ? { ...l, visibility: ev.target.value } : l
                      )
                    )
                  }
                  style={{ ...smallInput, fontSize: "0.6rem" }}
                >
                  {["bright", "gloomy", "dark"].map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <ModeButton
                $active={false}
                onClick={() => {
                  setCurrentLevelIdx(idx);
                  setEditingMonsterId(null);
                  setEditingNpcId(null);
                  setEditingLadderId(null);
                  setEditingItemId(null);
                  setSelectedNpcId(null);
                }}
              >
                {lv.name}
              </ModeButton>
            )}
            {levels.length > 1 && (
              <span
                style={{
                  color: c.danger,
                  cursor: "pointer",
                  fontSize: "0.55rem",
                  marginLeft: "0.15rem",
                  padding: "0 0.2rem",
                }}
                title="Delete level"
                onClick={() => {
                  if (window.confirm(`Delete ${lv.name}?`)) removeLevel(idx);
                }}
              >
                ✕
              </span>
            )}
          </div>
        ))}
        <ModeButton onClick={addLevel} style={{ fontSize: "0.6rem" }}>
          + Level
        </ModeButton>
      </div>

      <EditWindow>
        <BoardScroller>
        <Board>
          <div
            ref={boardRef}
            style={{ position: "relative", userSelect: "none", touchAction: panMode ? "pan-x pan-y" : "none" }}
            onPointerDown={(e) => { if (panMode && e.pointerType === 'touch') return; handleBoardMouseDown(e); }}
            onPointerMove={(e) => { if (panMode && e.pointerType === 'touch') return; handleBoardMouseMove(e); }}
            onPointerLeave={() => { setHoverInfo(null); setPlacementPreview(null); }}
          >
            <canvas
              ref={boardCanvasRef}
              width={(map.cols ? map.cols * map.grid : (tiles[0]?.length ?? 0) * map.grid)}
              height={tiles.length * map.grid}
              style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none" }}
            />
            {tiles.map((row, r) => (
              <Row
                key={r}
                width={map.cols ? map.cols * map.grid : row.length * map.grid}
                height={map.grid}
                onClick={(e) => mode !== "tiles" && mode !== "decoration" && mode !== "overlay" && handleEntityClick(e, r)}
                style={{
                  cursor: pickingTileFor
                    ? "crosshair"
                    : (mode === "tiles" || mode === "decoration" || mode === "overlay")
                      ? "crosshair"
                      : "cell",
                  position: "relative",
                }}
              >
                {npcPlacementsInRow(r).map((p) => {
                  const isDragging = dragEntityPreview?.entity.id === p.id;
                  const opacity = isDragging ? 0.25 : 1;
                  const bgColor =
                    p.background_color || p.Npc?.background_color || "#4488cc";
                  const sp = p.Npc?.sprite;
                  return (
                    <div
                      key={p.id}
                      style={{
                        position: "absolute",
                        left: p.x + map.grid / 2,
                        top: map.grid / 2,
                        transform: "translate(-50%, -50%)",
                        width: 18,
                        height: 18,
                        background: sp ? "transparent" : bgColor,
                        border: `2px solid ${editingNpcId === p.id ? "#fff" : "rgba(255,255,255,0.7)"}`,
                        borderRadius: "50%",
                        overflow: "hidden",
                        pointerEvents: "none",
                        zIndex: 5,
                        opacity,
                      }}
                    >
                      {sp && (
                        <img
                          src={spriteImagePath(sp)}
                          alt=""
                          style={{
                            width: "100%",
                            height: "100%",
                            imageRendering: "pixelated",
                            objectFit: "contain",
                          }}
                        />
                      )}
                    </div>
                  );
                })}
                {entitiesInRow(r).map((entity) => {
                  const isDragging = dragEntityPreview?.entity.id === entity.id;
                  const opacity = isDragging ? 0.25 : 1;
                  return entity.entityType === "ladder" ? (
                    <div
                      key={entity.id}
                      style={{
                        position: "absolute",
                        left: entity.x + map.grid / 2,
                        top: map.grid / 2,
                        transform: "translate(-50%, -50%)",
                        width: 14,
                        height: 14,
                        background: entity.backgroundColor || "#c8a84b",
                        border: "1px solid rgba(255,255,255,0.8)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 9,
                        color: "#000",
                        pointerEvents: "none",
                        zIndex: 5,
                        opacity,
                      }}
                    >
                      {entity.direction === "up" ? "▲" : "▼"}
                    </div>
                  ) : entity.entityType === "exit" ? (
                    <div
                      key={entity.id}
                      style={{
                        position: "absolute",
                        left: entity.x + map.grid / 2,
                        top: map.grid / 2,
                        transform: "translate(-50%, -50%)",
                        width: 14,
                        height: 14,
                        background: "#0d2e14",
                        border: "1px solid #44cc66",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 9,
                        color: "#44cc66",
                        pointerEvents: "none",
                        zIndex: 5,
                        opacity,
                      }}
                    >
                      ⬆
                    </div>
                  ) : (
                    <EntityDot
                      key={entity.id}
                      color={entity.backgroundColor}
                      style={{
                        left: entity.x + map.grid / 2,
                        top: map.grid / 2,
                        opacity,
                      }}
                    />
                  );
                })}
              </Row>
            ))}
            {/* Boundary rectangles — monsters from entities, NPCs from npcPlacements */}
            {mode === "monsters" &&
              entities
                .filter(
                  (e) =>
                    e.entityType === "monster" && e.boundary?.w && e.boundary?.h
                )
                .map((e) => (
                  <div
                    key={`boundary_${e.id}`}
                    style={{
                      position: "absolute",
                      left: e.boundary.x * map.grid,
                      top: e.boundary.y * map.grid,
                      width: e.boundary.w * map.grid,
                      height: e.boundary.h * map.grid,
                      border: `1px dashed ${e.backgroundColor || "#aaa"}`,
                      background: `${e.backgroundColor || "#aaa"}11`,
                      pointerEvents: "none",
                      zIndex: 4,
                      boxSizing: "border-box",
                    }}
                  />
                ))}
            {mode === "npcs" &&
              levelNpcs
                .filter((p) => p.boundary?.w && p.boundary?.h)
                .map((p) => {
                  const bg = p.background_color || "#4488cc";
                  return (
                    <div
                      key={`boundary_${p.id}`}
                      style={{
                        position: "absolute",
                        left: p.boundary.x * map.grid,
                        top: p.boundary.y * map.grid,
                        width: p.boundary.w * map.grid,
                        height: p.boundary.h * map.grid,
                        border: `1px dashed ${bg}`,
                        background: `${bg}11`,
                        pointerEvents: "none",
                        zIndex: 4,
                        boxSizing: "border-box",
                      }}
                    />
                  );
                })}
            {/* Corner resize handles for the editing entity */}
            {(mode === "monsters" || mode === "npcs") &&
              (() => {
                const editingId =
                  mode === "monsters" ? editingMonsterId : editingNpcId;
                if (!editingId) return null;
                const isNpcMode = mode === "npcs";
                const rec = isNpcMode
                  ? npcPlacements.find(
                      (p) =>
                        p.id === editingId && p.boundary?.w && p.boundary?.h
                    )
                  : entities.find(
                      (en) =>
                        en.id === editingId && en.boundary?.w && en.boundary?.h
                    );
                if (!rec) return null;
                const b = rec.boundary;
                const g = map.grid;
                const bx = b.x * g,
                  by = b.y * g;
                const bw = b.w * g,
                  bh = b.h * g;
                const handleColor = isNpcMode
                  ? rec.background_color || "#4488cc"
                  : rec.backgroundColor || "#aaa";
                // Each corner stores the opposite corner's tile coords as the resize anchor
                return [
                  ["tl", bx, by, b.x + b.w, b.y + b.h],
                  ["tr", bx + bw, by, b.x, b.y + b.h],
                  ["bl", bx, by + bh, b.x + b.w, b.y],
                  ["br", bx + bw, by + bh, b.x, b.y],
                ].map(([corner, hx, hy, anchorTileX, anchorTileY]) => (
                  <div
                    key={`bh_${corner}`}
                    onMouseDown={(ev) => {
                      ev.stopPropagation();
                      ev.preventDefault();
                      boundaryResizeRef.current = {
                        entityId: rec.id,
                        anchorTileX,
                        anchorTileY,
                        isNpcPlacement: isNpcMode,
                      };
                    }}
                    style={{
                      position: "absolute",
                      left: hx - 5,
                      top: hy - 5,
                      width: 10,
                      height: 10,
                      background: handleColor,
                      border: "1px solid rgba(255,255,255,0.85)",
                      cursor: "nwse-resize",
                      zIndex: 15,
                      boxSizing: "border-box",
                    }}
                  />
                ));
              })()}
            {/* Boundary draw preview while dragging */}
            {boundaryDrawPreview &&
              (() => {
                const { startTile, endTile } = boundaryDrawPreview;
                const minCol = Math.min(startTile.col, endTile.col);
                const maxCol = Math.max(startTile.col, endTile.col);
                const minRow = Math.min(startTile.row, endTile.row);
                const maxRow = Math.max(startTile.row, endTile.row);
                const editingId =
                  mode === "monsters" ? editingMonsterId : editingNpcId;
                const ent =
                  mode === "npcs"
                    ? npcPlacements.find((p) => p.id === editingId)
                    : entities.find((en) => en.id === editingId);
                const borderColor =
                  (mode === "npcs"
                    ? ent?.background_color
                    : ent?.backgroundColor) || "#aaa";
                return (
                  <div
                    style={{
                      position: "absolute",
                      left: minCol * map.grid,
                      top: minRow * map.grid,
                      width: (maxCol - minCol + 1) * map.grid,
                      height: (maxRow - minRow + 1) * map.grid,
                      border: `2px dashed ${borderColor}`,
                      background: `${borderColor}22`,
                      pointerEvents: "none",
                      zIndex: 12,
                      boxSizing: "border-box",
                    }}
                  />
                );
              })()}
            {/* Placement tile markers — trigger tile (!) and NPC position (N) per interaction area */}
            {mode === "npcs" &&
              levelNpcs.flatMap((p) =>
                (p.interaction_areas || []).flatMap((a, ai) => {
                  const color = p.background_color || "#4488cc";
                  const markers = [];
                  if (a.tile)
                    markers.push(
                      <TileMarker
                        key={`area_${p.id}_${ai}_tile`}
                        tile={a.tile}
                        grid={map.grid}
                        color={color}
                        glyph="!"
                        dashed={false}
                      />
                    );
                  if (a.npc_position)
                    markers.push(
                      <TileMarker
                        key={`area_${p.id}_${ai}_pos`}
                        tile={a.npc_position}
                        grid={map.grid}
                        color={color}
                        glyph="N"
                        dashed={true}
                      />
                    );
                  return markers;
                })
              )}
            {dragEntityPreview &&
              (() => {
                const { entity, x, y } = dragEntityPreview;
                const cx = x + map.grid / 2;
                const cy = y + map.grid / 2;
                const isLadder = entity.entityType === "ladder";
                const isExit = entity.entityType === "exit";
                if (isLadder)
                  return (
                    <div
                      key="entity-ghost"
                      style={{
                        position: "absolute",
                        left: cx,
                        top: cy,
                        transform: "translate(-50%, -50%)",
                        width: 14,
                        height: 14,
                        background: entity.backgroundColor || "#c8a84b",
                        border: "2px dashed rgba(255,255,255,0.9)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 9,
                        color: "#000",
                        pointerEvents: "none",
                        zIndex: 20,
                        opacity: 0.85,
                      }}
                    >
                      {entity.direction === "up" ? "▲" : "▼"}
                    </div>
                  );
                if (isExit)
                  return (
                    <div
                      key="entity-ghost"
                      style={{
                        position: "absolute",
                        left: cx,
                        top: cy,
                        transform: "translate(-50%, -50%)",
                        width: 14,
                        height: 14,
                        background: "#0d2e14",
                        border: "2px dashed #44cc66",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 9,
                        color: "#44cc66",
                        pointerEvents: "none",
                        zIndex: 20,
                        opacity: 0.85,
                      }}
                    >
                      ⬆
                    </div>
                  );
                return (
                  <div
                    key="entity-ghost"
                    style={{
                      position: "absolute",
                      left: cx,
                      top: cy,
                      transform: "translate(-50%, -50%)",
                      width: 12,
                      height: 12,
                      background: entity.backgroundColor,
                      border: "2px dashed rgba(255,255,255,0.9)",
                      borderRadius: "50%",
                      pointerEvents: "none",
                      zIndex: 20,
                      opacity: 0.85,
                    }}
                  />
                );
              })()}
            {/* Overlay tile layer — rendered above entities */}
            <canvas
              ref={overlayCanvasRef}
              width={(map.cols ? map.cols * map.grid : (tiles[0]?.length ?? 0) * map.grid)}
              height={tiles.length * map.grid}
              style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none", zIndex: 6 }}
            />
            {dragStart &&
              dragEnd &&
              (mode === "tiles" || mode === "decoration" || mode === "overlay") &&
              (() => {
                const stamp = copyMode
                  ? null
                  : selectedBrush.kind === "stamp"
                    ? getStamp(selectedBrush.stampId)
                    : selectedBrush.kind === "clipboard" && clipboard
                      ? clipboard
                      : null;
                const minRow = stamp ? dragStart.row : Math.min(dragStart.row, dragEnd.row);
                const maxRow = stamp ? dragStart.row + stamp.h - 1 : Math.max(dragStart.row, dragEnd.row);
                const minCol = stamp ? dragStart.col : Math.min(dragStart.col, dragEnd.col);
                const maxCol = stamp ? dragStart.col + stamp.w - 1 : Math.max(dragStart.col, dragEnd.col);
                const previewColor = copyMode ? colors("accent") : tileMode === "tileset" || stamp ? "#ffffff" : color;
                return (
                  <div
                    style={{
                      position: "absolute",
                      left: minCol * map.grid,
                      top: minRow * map.grid,
                      width: (maxCol - minCol + 1) * map.grid,
                      height: (maxRow - minRow + 1) * map.grid,
                      background: `${previewColor}33`,
                      border: `2px solid ${previewColor}`,
                      boxSizing: "border-box",
                      pointerEvents: "none",
                      zIndex: 10,
                    }}
                  />
                );
              })()}
            {stampDragPreview && (
              <div
                style={{
                  position: "absolute",
                  left: stampDragPreview.col * map.grid,
                  top: stampDragPreview.row * map.grid,
                  width: stampDragPreview.w * map.grid,
                  height: stampDragPreview.h * map.grid,
                  background: stampDragRef.current?.hasMoved ? "#44cc8833" : "#cc444433",
                  border: `2px solid ${stampDragRef.current?.hasMoved ? "#44cc88" : "#cc4444"}`,
                  boxSizing: "border-box",
                  pointerEvents: "none",
                  zIndex: 10,
                }}
              />
            )}
            {placementPreview && !dragStart && !stampDragPreview && (
              <div
                style={{
                  position: "absolute",
                  left: placementPreview.col * map.grid,
                  top: placementPreview.row * map.grid,
                  width: placementPreview.w * map.grid,
                  height: placementPreview.h * map.grid,
                  background: "#ffffff22",
                  border: "2px dashed #ffffff",
                  boxSizing: "border-box",
                  pointerEvents: "none",
                  zIndex: 10,
                }}
              />
            )}
          </div>
        </Board>
        </BoardScroller>

        {hoverInfo && (
          <div
            style={{
              position: "fixed",
              left: hoverInfo.clientX + 14,
              top: hoverInfo.clientY + 14,
              background: colors("background", 0.92),
              color: c.text,
              border: `1px solid ${c.border}`,
              padding: "0.25rem 0.5rem",
              fontSize: "0.62rem",
              pointerEvents: "none",
              zIndex: 100,
              whiteSpace: "nowrap",
            }}
          >
            {hoverInfo.text}
          </div>
        )}

        <DockablePanel id="sidebar" title="Inspector" edge="right">
        <Sidebar>
          {mode === "tiles" && (
            <PaintControls {...brush} solid={solid} onSolidChange={setSolid} />
          )}
          {mode === "decoration" && (
            <>
              <SidebarLabel>Decoration Tiles</SidebarLabel>
              <PaintControls
                {...brush}
                hint="Drawn above base tiles, below entities. Never solid."
                erase={decorationErase}
                onEraseChange={setDecorationErase}
                opacity={decorationOpacity}
                onOpacityChange={setDecorationOpacity}
              />
            </>
          )}
          {mode === "overlay" && (
            <>
              <SidebarLabel>Overlay Tiles</SidebarLabel>
              <PaintControls
                {...brush}
                hint="Drawn above all entities. Never solid."
                erase={overlayErase}
                onEraseChange={setOverlayErase}
                opacity={overlayOpacity}
                onOpacityChange={setOverlayOpacity}
              />
            </>
          )}
          {mode === "npcs" && (
            <>
              {!dungeonUUID ? (
                <div style={{ fontSize: "0.65rem", color: c.muted }}>
                  Save the dungeon first to place NPCs.
                </div>
              ) : (
                <>
                  <SidebarLabel>NPC Library</SidebarLabel>
                  {dbNpcs.length === 0 && (
                    <div style={{ fontSize: "0.62rem", color: c.muted }}>
                      No NPCs found. Create them in Admin → NPCs.
                    </div>
                  )}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.25rem",
                      marginBottom: "0.75rem",
                      maxHeight: 180,
                      overflowY: "auto",
                    }}
                  >
                    {dbNpcs.map((n) => (
                      <div
                        key={n.id}
                        onClick={() => {
                          setSelectedNpcId(selectedNpcId === n.id ? null : n.id);
                          setEditingNpcId(null);
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.4rem",
                          padding: "0.3rem 0.4rem",
                          cursor: "pointer",
                          background:
                            selectedNpcId === n.id ? c.chrome : "transparent",
                          border: `1px solid ${selectedNpcId === n.id ? c.border : "transparent"}`,
                          fontSize: "0.65rem",
                        }}
                      >
                        <div
                          style={{
                            width: 24,
                            height: 24,
                            background: n.background_color || "#4488cc",
                            borderRadius: "50%",
                            flexShrink: 0,
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {n.sprite && (
                            <img
                              src={spriteImagePath(n.sprite)}
                              alt=""
                              style={{
                                width: "100%",
                                height: "100%",
                                imageRendering: "pixelated",
                                objectFit: "contain",
                              }}
                            />
                          )}
                        </div>
                        <span
                          style={{
                            color: selectedNpcId === n.id ? c.text : c.muted,
                          }}
                        >
                          {n.name}
                        </span>
                      </div>
                    ))}
                  </div>
                  {selectedNpcId && (
                    <div
                      style={{
                        fontSize: "0.58rem",
                        color: colors("accent"),
                        marginBottom: "0.75rem",
                      }}
                    >
                      ↖ click map to place
                    </div>
                  )}
                  <SidebarLabel>Placed NPCs ({levelNpcs.length})</SidebarLabel>
                  <div style={{ fontSize: "0.65rem" }}>
                    {levelNpcs.map((p) => {
                      const isExpanded = editingNpcId === p.id;
                      const bg =
                        p.background_color ||
                        p.Npc?.background_color ||
                        "#4488cc";
                      const npcName = p.Npc?.name || p.npc_id;
                      return (
                        <div key={p.id} style={{ marginBottom: "0.25rem" }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.3rem",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              setEditingNpcId(isExpanded ? null : p.id)
                            }
                          >
                            <div
                              style={{
                                width: 10,
                                height: 10,
                                background: bg,
                                borderRadius: "50%",
                                flexShrink: 0,
                              }}
                            />
                            <span
                              style={{
                                color: isExpanded ? c.text : c.muted,
                                flex: 1,
                              }}
                            >
                              {npcName}
                            </span>
                            <span
                              style={{
                                color: c.danger,
                                cursor: "pointer",
                                marginLeft: "auto",
                              }}
                              onClick={(ev) => {
                                ev.stopPropagation();
                                if (!window.confirm(`Remove ${npcName}?`))
                                  return;
                                setNpcPlacements((prev) =>
                                  prev.filter((x) => x.id !== p.id)
                                );
                                if (dungeonUUIDRef.current)
                                  dungeonNpcDelete(
                                    null,
                                    dungeonUUIDRef.current,
                                    p.id
                                  );
                                if (editingNpcId === p.id)
                                  setEditingNpcId(null);
                              }}
                            >
                              ✕
                            </span>
                          </div>
                          {isExpanded && (
                            <div
                              style={{
                                marginTop: "0.5rem",
                                paddingLeft: "0.5rem",
                                borderLeft: `2px solid ${c.border}`,
                              }}
                            >
                              <SidebarLabel>Boundary</SidebarLabel>
                              {p.boundary?.w && p.boundary?.h ? (
                                <div style={{ marginBottom: "0.5rem" }}>
                                  <div
                                    style={{
                                      fontSize: "0.62rem",
                                      color: c.muted,
                                      marginBottom: "0.3rem",
                                    }}
                                  >
                                    {p.boundary.w} × {p.boundary.h} tiles at (
                                    {p.boundary.x}, {p.boundary.y})
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "0.58rem",
                                      color: c.dim,
                                      marginBottom: "0.3rem",
                                    }}
                                  >
                                    drag corners to resize · drag on map to
                                    redraw
                                  </div>
                                  <span
                                    style={{
                                      fontSize: "0.6rem",
                                      color: c.danger,
                                      cursor: "pointer",
                                    }}
                                    onClick={() =>
                                      commitPlacementPatch(p.id, {
                                        boundary: null,
                                      })
                                    }
                                  >
                                    Remove boundary
                                  </span>
                                </div>
                              ) : (
                                <div style={{ marginBottom: "0.5rem" }}>
                                  <div
                                    style={{
                                      fontSize: "0.58rem",
                                      color: c.dim,
                                      marginBottom: "0.3rem",
                                    }}
                                  >
                                    drag on map to draw, or:
                                  </div>
                                  <span
                                    style={{
                                      fontSize: "0.62rem",
                                      color: c.muted,
                                      cursor: "pointer",
                                      display: "block",
                                    }}
                                    onClick={() => {
                                      const ex = Math.floor(p.x / map.grid);
                                      const ey = Math.floor(p.y / map.grid);
                                      const boundary = {
                                        x: Math.max(0, ex - 2),
                                        y: Math.max(0, ey - 2),
                                        w: 4,
                                        h: 4,
                                      };
                                      commitPlacementPatch(p.id, { boundary });
                                    }}
                                  >
                                    + Add 4×4 default
                                  </span>
                                </div>
                              )}

                              <SidebarLabel>Interaction Areas</SidebarLabel>
                              {(p.interaction_areas || []).map((area, ai) => (
                                <div
                                  key={area.id ?? ai}
                                  style={{
                                    marginBottom: "0.75rem",
                                    paddingLeft: "0.5rem",
                                    borderLeft: `2px solid ${c.border}`,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <span
                                      style={{ fontSize: "0.62rem", color: c.muted }}
                                    >
                                      Area {ai + 1}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: "0.6rem",
                                        color: c.danger,
                                        cursor: "pointer",
                                      }}
                                      onClick={() => removeArea(p, ai)}
                                    >
                                      Remove area
                                    </span>
                                  </div>
                                  {renderAreaTilePicker(
                                    p,
                                    ai,
                                    "tile",
                                    "Trigger Tile",
                                    "Player approaching this tile triggers the NPC."
                                  )}
                                  {area.tile &&
                                    renderAreaTilePicker(
                                      p,
                                      ai,
                                      "npc_position",
                                      "NPC Position",
                                      "Optional tile the NPC walks to for the interaction. Defaults to the trigger tile."
                                    )}
                                  <DialogEditor
                                    label="Area Dialogs"
                                    dialogs={area.dialogs || []}
                                    onChange={(v) => updateArea(p, ai, { dialogs: v })}
                                  />
                                </div>
                              ))}
                              <span
                                style={{
                                  fontSize: "0.62rem",
                                  color: c.muted,
                                  cursor: "pointer",
                                  display: "block",
                                  marginTop: "0.25rem",
                                }}
                                onClick={() => addArea(p)}
                              >
                                + Add interaction area
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}
          {mode !== "tiles" && mode !== "decoration" && mode !== "overlay" && mode !== "npcs" && (
            <>
              <SidebarLabel>{mode} (click grid to place/remove)</SidebarLabel>
              {mode === "items" && (
                <div style={{ marginBottom: "0.75rem" }}>
                  <SidebarLabel>Type</SidebarLabel>
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}>
                    {ITEM_TYPES.map((t) => (
                      <div
                        key={t.value}
                        onClick={() => setSelectedItemType(t)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.4rem",
                          padding: "0.25rem 0.4rem",
                          cursor: "pointer",
                          background: selectedItemType.value === t.value ? c.chrome : "transparent",
                          border: `1px solid ${selectedItemType.value === t.value ? c.border : "transparent"}`,
                          fontSize: "0.65rem",
                        }}
                      >
                        <div style={{ width: 10, height: 10, background: t.color, flexShrink: 0 }} />
                        <span style={{ color: selectedItemType.value === t.value ? c.text : c.muted }}>
                          {t.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div
                style={{
                  fontSize: "0.65rem",
                  color: c.muted,
                  marginTop: "0.5rem",
                }}
              >
                {
                  entities.filter(
                    (e) => e.entityType === ENTITY_DEFAULTS[mode]?.entityType
                  ).length
                }{" "}
                placed
              </div>
              <div style={{ marginTop: "1rem", fontSize: "0.65rem" }}>
                <SidebarLabel>Placed {mode}</SidebarLabel>
                {entities
                  .filter(
                    (e) => e.entityType === ENTITY_DEFAULTS[mode]?.entityType
                  )
                  .map((e) => {
                    const isExpanded =
                      (mode === "monsters" && editingMonsterId === e.id) ||
                      (mode === "ladders" && editingLadderId === e.id) ||
                      (mode === "items" && editingItemId === e.id);
                    const toggle = () => {
                      if (mode === "monsters")
                        setEditingMonsterId(isExpanded ? null : e.id);
                      if (mode === "ladders")
                        setEditingLadderId(isExpanded ? null : e.id);
                      if (mode === "items")
                        setEditingItemId(isExpanded ? null : e.id);
                    };
                    return (
                      <div key={e.id} style={{ marginBottom: "0.25rem" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.3rem",
                            cursor:
                              mode === "monsters" ||
                              mode === "ladders" ||
                              mode === "items"
                                ? "pointer"
                                : "default",
                          }}
                          onClick={toggle}
                        >
                          {e.entityType === "ladder" ? (
                            <div
                              style={{
                                width: 10,
                                height: 10,
                                background: e.backgroundColor,
                                flexShrink: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 7,
                                color: "#000",
                              }}
                            >
                              {e.direction === "up" ? "▲" : "▼"}
                            </div>
                          ) : (
                            <div
                              style={{
                                width: 10,
                                height: 10,
                                background: e.backgroundColor,
                                borderRadius: "50%",
                                flexShrink: 0,
                              }}
                            />
                          )}
                          <span
                            style={{ color: isExpanded ? c.text : c.muted }}
                          >
                            {e.entityType === "ladder"
                              ? `→ ${levels[e.targetLevel ?? 0]?.name ?? `Floor ${(e.targetLevel ?? 0) + 1}`}`
                              : e.name || e.id}
                          </span>
                          <span
                            style={{
                              color: c.danger,
                              cursor: "pointer",
                              marginLeft: "auto",
                            }}
                            onClick={(ev) => {
                              ev.stopPropagation();
                              pushHistory();
                              setEntities(
                                entities.filter((en) => en.id !== e.id)
                              );
                              if (editingMonsterId === e.id)
                                setEditingMonsterId(null);
                              if (editingNpcId === e.id) setEditingNpcId(null);
                              if (editingLadderId === e.id)
                                setEditingLadderId(null);
                              if (editingItemId === e.id)
                                setEditingItemId(null);
                            }}
                          >
                            ✕
                          </span>
                        </div>

                        {mode === "monsters" && editingMonsterId === e.id && (
                          <div
                            style={{
                              marginTop: "0.5rem",
                              paddingLeft: "0.5rem",
                              borderLeft: `2px solid ${c.border}`,
                            }}
                          >
                            <SidebarLabel>Name</SidebarLabel>
                            <input
                              value={e.name || ""}
                              onChange={(ev) =>
                                updateEntity(e.id, { name: ev.target.value || undefined })
                              }
                              placeholder="e.g. Cave Troll"
                              style={{
                                ...smallInput,
                                width: "100%",
                                marginBottom: "0.5rem",
                                boxSizing: "border-box",
                              }}
                            />

                            <SidebarLabel>HP</SidebarLabel>
                            <input
                              type="number"
                              min={1}
                              value={e.health ?? 30}
                              onChange={(ev) =>
                                updateEntity(e.id, {
                                  health: Number(ev.target.value),
                                })
                              }
                              style={{
                                ...smallInput,
                                width: "100%",
                                marginBottom: "0.5rem",
                                boxSizing: "border-box",
                              }}
                            />

                            <SidebarLabel>Damage</SidebarLabel>
                            <input
                              type="number"
                              min={0}
                              value={e.damage ?? 5}
                              onChange={(ev) =>
                                updateEntity(e.id, {
                                  damage: Number(ev.target.value),
                                })
                              }
                              style={{
                                ...smallInput,
                                width: "100%",
                                marginBottom: "0.5rem",
                                boxSizing: "border-box",
                              }}
                            />

                            <SidebarLabel>Speed</SidebarLabel>
                            <div style={{ display: "flex", gap: "0.25rem", marginBottom: "0.5rem" }}>
                              {[
                                { label: "Slow", value: 1 },
                                { label: "Normal", value: 2 },
                                { label: "Fast", value: 3 },
                                { label: "Very Fast", value: 4 },
                              ].map(({ label, value }) => (
                                <ModeButton
                                  key={value}
                                  $active={(e.stride ?? 1) === value}
                                  onClick={() => updateEntity(e.id, { stride: value })}
                                  style={{ flex: 1, fontSize: "0.55rem" }}
                                >
                                  {label}
                                </ModeButton>
                              ))}
                            </div>

                            <SidebarLabel>Boundary</SidebarLabel>
                            {e.boundary?.w && e.boundary?.h ? (
                              <div style={{ marginBottom: "0.5rem" }}>
                                <div
                                  style={{
                                    fontSize: "0.62rem",
                                    color: c.muted,
                                    marginBottom: "0.3rem",
                                  }}
                                >
                                  {e.boundary.w} × {e.boundary.h} tiles at (
                                  {e.boundary.x}, {e.boundary.y})
                                </div>
                                <div
                                  style={{
                                    fontSize: "0.58rem",
                                    color: c.dim,
                                    marginBottom: "0.3rem",
                                  }}
                                >
                                  drag corners to resize · drag on map to redraw
                                </div>
                                <span
                                  style={{
                                    fontSize: "0.6rem",
                                    color: c.danger,
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    updateEntity(e.id, { boundary: undefined })
                                  }
                                >
                                  Remove boundary
                                </span>
                              </div>
                            ) : (
                              <div style={{ marginBottom: "0.5rem" }}>
                                <div
                                  style={{
                                    fontSize: "0.58rem",
                                    color: c.dim,
                                    marginBottom: "0.3rem",
                                  }}
                                >
                                  drag on map to draw, or:
                                </div>
                                <span
                                  style={{
                                    fontSize: "0.62rem",
                                    color: c.muted,
                                    cursor: "pointer",
                                    display: "block",
                                  }}
                                  onClick={() => {
                                    const ex = Math.floor(e.x / map.grid);
                                    const ey = Math.floor(e.y / map.grid);
                                    updateEntity(e.id, {
                                      boundary: {
                                        x: Math.max(0, ex - 2),
                                        y: Math.max(0, ey - 2),
                                        w: 4,
                                        h: 4,
                                      },
                                    });
                                  }}
                                >
                                  + Add 4×4 default
                                </span>
                              </div>
                            )}

                            <SidebarLabel>Direction change (ms)</SidebarLabel>
                            <input
                              type="number"
                              min={500}
                              step={500}
                              value={e.boundaryPeriod ?? ""}
                              placeholder="random 3–5 s"
                              onChange={(ev) => {
                                const v = Number(ev.target.value);
                                updateEntity(e.id, {
                                  boundaryPeriod: v || undefined,
                                });
                              }}
                              style={{
                                ...smallInput,
                                width: "100%",
                                boxSizing: "border-box",
                              }}
                            />

                            <SidebarLabel>Sprite</SidebarLabel>
                            <div style={{ display: "flex", gap: "0.4rem", marginBottom: "0.5rem" }}>
                              {MONSTER_SPRITES.map((sp) => (
                                <button
                                  key={sp}
                                  onClick={() => updateEntity(e.id, { sprite: sp })}
                                  title={sp}
                                  style={{
                                    background: (e.sprite ?? "Skeleton") === sp ? c.accent : c.surface,
                                    border: `1px solid ${(e.sprite ?? "Skeleton") === sp ? c.accent : c.border}`,
                                    borderRadius: 4,
                                    padding: 2,
                                    cursor: "pointer",
                                  }}
                                >
                                  <img
                                    src={spriteImagePath(sp)}
                                    alt={sp}
                                    style={{
                                      width: 28,
                                      height: 28,
                                      imageRendering: "pixelated",
                                      display: "block",
                                      filter: (e.sprite ?? "Skeleton") === sp ? "none" : "grayscale(100%) opacity(0.4)",
                                    }}
                                  />
                                </button>
                              ))}
                            </div>
                            <SidebarLabel style={{ marginTop: "0.5rem" }}>Drops</SidebarLabel>
                            {(e.drops ?? []).map((drop, di) => (
                              <div key={di} style={{ display: "flex", flexDirection: "column", gap: "0.2rem", marginBottom: "0.4rem" }}>
                                <div style={{ display: "flex", gap: "0.25rem", alignItems: "center" }}>
                                  <select
                                    value={drop.type || "treasure"}
                                    onChange={(ev) => {
                                      const drops = [...(e.drops ?? [])];
                                      drops[di] = { ...drops[di], type: ev.target.value };
                                      updateEntity(e.id, { drops });
                                    }}
                                    style={{ ...smallInput, flex: 2 }}
                                  >
                                    {ITEM_TYPES.map((t) => (
                                      <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                  </select>
                                  <input
                                    placeholder="name"
                                    value={drop.name || ""}
                                    onChange={(ev) => {
                                      const drops = [...(e.drops ?? [])];
                                      drops[di] = { ...drops[di], name: ev.target.value };
                                      updateEntity(e.id, { drops });
                                    }}
                                    style={{ ...smallInput, flex: 3 }}
                                  />
                                  <span
                                    style={{ color: c.danger, cursor: "pointer", fontSize: "0.8rem", lineHeight: 1, flexShrink: 0 }}
                                    onClick={() => {
                                      const drops = (e.drops ?? []).filter((_, j) => j !== di);
                                      updateEntity(e.id, { drops });
                                    }}
                                  >×</span>
                                </div>
                                <div style={{ display: "flex", gap: "0.25rem", alignItems: "center" }}>
                                  <input
                                    type="number"
                                    min={0}
                                    placeholder="value"
                                    value={drop.value ?? 10}
                                    onChange={(ev) => {
                                      const drops = [...(e.drops ?? [])];
                                      drops[di] = { ...drops[di], value: Number(ev.target.value) };
                                      updateEntity(e.id, { drops });
                                    }}
                                    style={{ ...smallInput, flex: 1 }}
                                  />
                                  <input
                                    type="number"
                                    min={1}
                                    max={100}
                                    placeholder="% chance"
                                    value={drop.chance ?? 100}
                                    onChange={(ev) => {
                                      const drops = [...(e.drops ?? [])];
                                      drops[di] = { ...drops[di], chance: Number(ev.target.value) };
                                      updateEntity(e.id, { drops });
                                    }}
                                    style={{ ...smallInput, flex: 1 }}
                                  />
                                </div>
                              </div>
                            ))}
                            <span
                              style={{ fontSize: "0.62rem", color: c.muted, cursor: "pointer" }}
                              onClick={() => updateEntity(e.id, { drops: [...(e.drops ?? []), { type: "treasure", name: "", value: 10, chance: 100 }] })}
                            >+ Add drop</span>
                          </div>
                        )}

                        {mode === "ladders" && editingLadderId === e.id && (
                          <div
                            style={{
                              marginTop: "0.5rem",
                              paddingLeft: "0.5rem",
                              borderLeft: `2px solid ${c.border}`,
                            }}
                          >
                            <SidebarLabel>Direction</SidebarLabel>
                            <select
                              value={e.direction || "down"}
                              onChange={(ev) =>
                                updateEntity(e.id, {
                                  direction: ev.target.value,
                                })
                              }
                              style={{
                                ...smallInput,
                                marginBottom: "0.5rem",
                                width: "100%",
                              }}
                            >
                              <option value="down">▼ Down (deeper)</option>
                              <option value="up">▲ Up (surface)</option>
                            </select>

                            <SidebarLabel>Target Level</SidebarLabel>
                            <select
                              value={e.targetLevel ?? 0}
                              onChange={(ev) =>
                                updateEntity(e.id, {
                                  targetLevel: Number(ev.target.value),
                                })
                              }
                              style={{
                                ...smallInput,
                                width: "100%",
                                marginBottom: "0.5rem",
                              }}
                            >
                              {levels.map((lv, idx) =>
                                idx === currentLevelIdx ? null : (
                                  <option key={idx} value={idx}>
                                    {lv.name}
                                  </option>
                                )
                              )}
                            </select>

                            <SidebarLabel>Spawn X on target</SidebarLabel>
                            <input
                              type="number"
                              value={e.targetX ?? 40}
                              onChange={(ev) =>
                                updateEntity(e.id, {
                                  targetX: Number(ev.target.value),
                                })
                              }
                              style={{
                                ...smallInput,
                                width: "100%",
                                marginBottom: "0.5rem",
                                boxSizing: "border-box",
                              }}
                            />

                            <SidebarLabel>Spawn Y on target</SidebarLabel>
                            <input
                              type="number"
                              value={e.targetY ?? 40}
                              onChange={(ev) =>
                                updateEntity(e.id, {
                                  targetY: Number(ev.target.value),
                                })
                              }
                              style={{
                                ...smallInput,
                                width: "100%",
                                boxSizing: "border-box",
                                marginBottom: "0.5rem",
                              }}
                            />

                            <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", cursor: "pointer", color: c.text, fontSize: "0.65rem" }}>
                              <input
                                type="checkbox"
                                checked={!!e.oneWay}
                                onChange={(ev) => {
                                  if (ev.target.checked) {
                                    // Remove paired ladder and mark one-way
                                    if (e.pairedId) {
                                      const targetLvl = e.targetLevel ?? 0;
                                      setLevels((prev) =>
                                        prev.map((l, i) =>
                                          i === targetLvl
                                            ? { ...l, entities: (l.entities || []).filter((en) => en.id !== e.pairedId) }
                                            : l
                                        )
                                      );
                                    }
                                    updateEntity(e.id, { oneWay: true, pairedId: null });
                                  } else {
                                    // Create paired ladder and link it
                                    const pairedId = `ladder_${Date.now()}`;
                                    const withPair = { ...e, oneWay: false, pairedId };
                                    const paired = makePairedLadder(withPair, currentLevelIdx);
                                    const targetLvl = e.targetLevel ?? 0;
                                    setLevels((prev) =>
                                      prev.map((l, i) =>
                                        i === targetLvl
                                          ? { ...l, entities: [...(l.entities || []), paired] }
                                          : l
                                      )
                                    );
                                    updateEntity(e.id, { oneWay: false, pairedId });
                                  }
                                }}
                              />
                              One way
                            </label>
                          </div>
                        )}

                        {mode === "items" && editingItemId === e.id && (
                          <div
                            style={{
                              marginTop: "0.5rem",
                              paddingLeft: "0.5rem",
                              borderLeft: `2px solid ${c.border}`,
                            }}
                          >
                            <SidebarLabel>Name</SidebarLabel>
                            <input
                              value={e.name || ""}
                              onChange={(ev) =>
                                updateEntity(e.id, { name: ev.target.value })
                              }
                              placeholder={e.id}
                              style={{
                                width: "100%",
                                ...smallInput,
                                marginBottom: "0.5rem",
                                boxSizing: "border-box",
                              }}
                            />

                            <SidebarLabel>Type</SidebarLabel>
                            <select
                              value={e.treasureType || "gold"}
                              onChange={(ev) => {
                                const tt = ITEM_TYPES.find(
                                  (t) => t.value === ev.target.value
                                );
                                updateEntity(e.id, {
                                  treasureType: ev.target.value,
                                  backgroundColor:
                                    tt?.color ?? e.backgroundColor,
                                });
                              }}
                              style={{
                                ...smallInput,
                                width: "100%",
                                marginBottom: "0.5rem",
                              }}
                            >
                              {ITEM_TYPES.map((t) => (
                                <option key={t.value} value={t.value}>
                                  {t.label}
                                </option>
                              ))}
                            </select>

                            <SidebarLabel>Behavior</SidebarLabel>
                            <select
                              value={e.treasureBehavior || "normal"}
                              onChange={(ev) =>
                                updateEntity(e.id, {
                                  treasureBehavior: ev.target.value,
                                })
                              }
                              style={{
                                ...smallInput,
                                width: "100%",
                                marginBottom: "0.5rem",
                              }}
                            >
                              {ITEM_BEHAVIORS.map((b) => (
                                <option key={b.value} value={b.value}>
                                  {b.label}
                                </option>
                              ))}
                            </select>

                            <SidebarLabel>Value</SidebarLabel>
                            <input
                              type="number"
                              min={0}
                              value={e.value ?? 10}
                              onChange={(ev) =>
                                updateEntity(e.id, {
                                  value: Number(ev.target.value),
                                })
                              }
                              style={{
                                ...smallInput,
                                width: "100%",
                                boxSizing: "border-box",
                                marginBottom: "0.5rem",
                              }}
                            />

                            <SidebarLabel>Illumination (tiles)</SidebarLabel>
                            <input
                              type="number"
                              min={0}
                              max={20}
                              value={e.illumination ?? 0}
                              onChange={(ev) =>
                                updateEntity(e.id, {
                                  illumination: Number(ev.target.value),
                                })
                              }
                              style={{
                                ...smallInput,
                                width: "100%",
                                boxSizing: "border-box",
                              }}
                            />

                            <SidebarLabel>Lifespan (seconds, 0 = infinite)</SidebarLabel>
                            <input
                              type="number"
                              min={0}
                              value={e.lifespan ?? 0}
                              onChange={(ev) =>
                                updateEntity(e.id, {
                                  lifespan: Number(ev.target.value),
                                })
                              }
                              style={{
                                ...smallInput,
                                width: "100%",
                                boxSizing: "border-box",
                              }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </>
          )}
        </Sidebar>
        </DockablePanel>
      </EditWindow>

      {/* ── Publish panel ── */}
      <DockablePanel id="publish" title="Publish" edge="bottom">
      <div
        style={{
          marginTop: "2rem",
          borderTop: `1px solid ${c.border}`,
          paddingTop: "1.5rem",
          maxWidth: 560,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            marginBottom: publishOpen ? "0.75rem" : 0,
          }}
        >
          <SidebarLabel
            style={{
              fontSize: "0.75rem",
              marginBottom: 0,
              cursor: "pointer",
              userSelect: "none",
            }}
            onClick={() => setPublishOpen((v) => !v)}
            title="Toggle publish settings"
          >
            {publishOpen ? "▾" : "▸"} Publish to Server
          </SidebarLabel>
          <ModeButton
            onClick={handleSaveToDB}
            disabled={saveStatus === "saving"}
            style={{ background: saveStatus === "saved" ? c.success : undefined }}
          >
            {saveStatus === "saving"
              ? "Saving…"
              : saveStatus === "saved"
                ? "Saved!"
                : saveStatus === "error"
                  ? "Error — retry"
                  : id
                    ? "Update Dungeon"
                    : "Publish Dungeon"}
          </ModeButton>
        </div>

        {publishOpen && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
            marginBottom: "1rem",
          }}
        >
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <div style={{ flex: 1 }}>
              <SidebarLabel>Name</SidebarLabel>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Dark Caverns"
                style={{ width: "100%", ...inputStyle }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <SidebarLabel>Slug</SidebarLabel>
              <input
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="dark-caverns"
                disabled={!!id}
                style={{
                  width: "100%",
                  ...inputStyle,
                  background: id ? c.chrome : c.bg,
                }}
              />
            </div>
            <div style={{ width: 80 }}>
              <SidebarLabel>Radius (m)</SidebarLabel>
              <input
                type="number"
                value={radius}
                onChange={(e) => setRadius(Number(e.target.value))}
                style={{ width: "100%", ...inputStyle }}
              />
            </div>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontFamily: "Silkscreen, monospace", fontSize: "0.62rem", color: c.muted, marginBottom: "0.4rem", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={isGlobal}
              onChange={(e) => setIsGlobal(e.target.checked)}
            />
            Global dungeon (spawns near any player)
          </label>

          <div>
            {isAdmin && (
              <div
                style={{
                  display: "flex",
                  gap: "0.25rem",
                  marginBottom: "0.4rem",
                }}
              >
                <ModeButton
                  $active={locationMode === "address"}
                  onClick={() => setLocationMode("address")}
                  style={{ fontSize: "0.6rem", flex: 1 }}
                >
                  Address
                </ModeButton>
                <ModeButton
                  $active={locationMode === "coords"}
                  onClick={() => setLocationMode("coords")}
                  style={{ fontSize: "0.6rem", flex: 1 }}
                >
                  Coordinates
                </ModeButton>
              </div>
            )}
            {(!isAdmin || locationMode === "address") && (
              <>
                <SidebarLabel>Address</SidebarLabel>
                {hostAddresses.length === 0 ? (
                  <div style={{ fontSize: "0.65rem", color: c.muted }}>
                    No addresses found.
                  </div>
                ) : (
                  <select
                    value={addressId}
                    onChange={(e) => setAddressId(e.target.value)}
                    style={{ width: "100%", ...inputStyle }}
                  >
                    {hostAddresses.map((addr) => (
                      <option key={addr.id} value={addr.id}>
                        {addr.locationName} — {addr.text}
                      </option>
                    ))}
                  </select>
                )}
              </>
            )}
            {isAdmin && locationMode === "coords" && (
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <div style={{ flex: 1 }}>
                  <SidebarLabel>Latitude</SidebarLabel>
                  <input
                    type="number"
                    step="any"
                    value={adminLat}
                    onChange={(e) => setAdminLat(e.target.value)}
                    placeholder="e.g. 40.7128"
                    style={{
                      width: "100%",
                      ...inputStyle,
                      boxSizing: "border-box",
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <SidebarLabel>Longitude</SidebarLabel>
                  <input
                    type="number"
                    step="any"
                    value={adminLng}
                    onChange={(e) => setAdminLng(e.target.value)}
                    placeholder="e.g. -74.0060"
                    style={{
                      width: "100%",
                      ...inputStyle,
                      boxSizing: "border-box",
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        )}
      </div>
      </DockablePanel>
    </Container>
  );
};
